import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography, Divider, Stack, Chip } from "@mui/material";
import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTimer } from "../../Hooks/useTimerHook";
import { useSocketContext } from "../../context/SocketContext";
import logger from "../../logger/BatchedLogger";
// ICONS
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import { webAssistantSubscriptionDataUsage } from "../../Common/api.routes";
import { CALL_USER_LEFT, CONSULTANT_END_CALL, END_CALL, LEAVE_CALL, MUTE_YOURSELF, OFF_AUDIO_TO_WEB_AST, ON_AUDIO_TO_WEB_AST, RECORDING_ENDED, RECORDING_STARTED, ROOM_DESTROYED, UNMUTE_YOURSELF, USER_MUTE, WAST_CALL_TRANSFERRED, WEB_AST_CALL_INITIATION, WEB_AST_RECONNECT } from "../../Common/helper";
import CadisUnmuteIcon from "../../Components/CustomUnmuteIcon/CadisUnmuteIcon";
// IMAGES
import CadisDialog from "../../Components/Dialog/CadisDialog";
import CadisButton from "../../Components/Input/CadisButton";
import preventBackNavigation from "../../Components/NavigationBackDialog/PreventBackNavigation";
import useIsMobileDevice from "../../Hooks/useIsMobileDevice";
import apis from "../../HttpConfig/Api";
import ToggleComponent from "./ToggleComponent";

var zoomLevel = 100;
let bitrateTimeout;
var isSelfSecondary = true;

/*
 * Agora lifecycle and invariants
 * -----------------------------
 * - UIDs are a cross-client contract: consultant=1, web assistant/patient=2, Rokid=3.
 * - The media permission gate must close before Agora creates local tracks. Holding the
 *   temporary getUserMedia stream and an Agora track at the same time can fail on mobile.
 * - Consultant audio is subscribed only while this participant is in the main view.
 *   ON_AUDIO_TO_WEB_AST/OFF_AUDIO_TO_WEB_AST are authoritative for that routing decision.
 * - Browser autoplay and iOS interruption events belong to the global AgoraRTC object,
 *   while channel and remote-user events belong to the IAgoraRTCClient instance.
 * - Every failed initialization must close tracks and leave the channel before retrying.
 *
 * These values are module-scoped because socket callbacks and reconnect timers need the
 * latest Agora objects without waiting for a React render. They must be reset by cleanupAgora.
 */
var agoraClient = null;
var agoraSdk = null;
var agoraAutoplayFailedHandler = null;
var agoraAudioContextStateHandler = null;
var localAudioTrackAgora = null;
var localVideoTrackAgora = null;
var remoteUsers = {};
var agoraReconnectTimer = null;
var isManuallyReconnecting = false;  // prevents duplicate concurrent manual reconnect attempts
var networkQualityDownlinkLevel = 0;
var isConsultantAudioBlocked = false; // false=allowed (default/ON), true=blocked (OFF)
var audioControlSeq = 0; // monotonic counter — incremented on every ON/OFF event so only the latest wins
var hasPlayedJoinTone = false; // prevents replaying join tone and resetting timer on Agora reconnect

// Deterministic Agora UIDs per role
var AGORA_UID_CONSULTANT = 1;
var AGORA_UID_WEBASSISTANT = 2;
var AGORA_UID_ROKID = 3;

// recording variables
let mediaRecorder;

//data consumption variables
let prevBytesSent = 0;
var consultantIntervalId = null;
var consDataReceived = 0;
var dataSent = 0;
var wastIntervalDataSent = 0;
var consIntervalDataReceived = 0;
var timeoutId;

const WebAssistantCallScreen = () => {
	const { socketRef, failedPong, callToAssistantRef, webAsstRoomDetails, sessionIdRef, roomData, isMuteConsultant, endCallDialog, callJoinToneRef, setRoomDetails, setInCommingCall, setCallToAssistant, setUserDetails, openInviteDialog, openReconnectDialog, setIsConferenceCall, openEndCallPopup, setEndCallDialog, agoraConfig, setAgoraConfig } = useSocketContext(); // socket context custom hook
	const { seconds, minutes, hours, handleStart, handleReset } = useTimer(); // timer custom hook
	const navigate = useNavigate();
	const { state } = useLocation();
	const { t } = useTranslation();
	const { navigationBackPopup, setNavigationBackPopup } = preventBackNavigation("/webAssistantJoin");

	// Refs for video and canvas elements
	const remoteVideoRef = useRef();
	const remoteAudioRef = useRef();
	const localVideoRef = useRef();
	const joinInitiatedRef = useRef(false);
	const isJoiningRef = useRef(false);       // mutex: prevents concurrent Agora join calls
	const lastJoinedTokenRef = useRef(null);  // tracks the last token used for Agora join
	const hasExplicitAudioUnlockRef = useRef(false);

	// State variables
	const [isRemoteVideo, setIsRemoteVideo] = useState(false);
	// True only when the consultant is present in the channel but has turned their camera OFF
	// (video unpublished while still joined). Distinguishes camera-off from not-yet-connected,
	// so we can show an avatar instead of "Connecting...".
	const [isConsultantCameraOff, setIsConsultantCameraOff] = useState(false);
	// True when the web assistant has turned their OWN camera off — show an avatar over the local video.
	const [isSelfCameraOff, setIsSelfCameraOff] = useState(false);
	const [isPluginAttached, setIsPluginAttached] = useState(false);
	const [remoteAudioTrack, setRemoteAudioTrack] = useState();
	const [localAudioTrack, setLocalAudioTrack] = useState();
	const [userLeftAlert, setUserLeftAlert] = useState(false);
	const [showMuteIcon, setShowMuteIcon] = useState(false);
	const [container1, setContainer1] = useState("container1");
	const [container2, setContainer2] = useState("container2");
	const [recordingStarted, setRecordingStarted] = useState(false);
	const [recordingMute, setRecordingMute] = useState(false);
	const [localVideoStream, setLocalVideoStream] = useState(null);
	const [webAsstSelfMute, setWebAsstSelfMute] = useState(sessionStorage.getItem("wastSelfMuted") === "true");
	const [dataUsageTimer, setDataUsageTimer] = useState(0);
	const [isFrontCamera, setIsFrontCamera] = useState(true);
	const [showMediaAccessPopup, setShowMediaAccessPopup] = useState(() => {
		const mediaAccessGranted = sessionStorage.getItem("mediaAccessGranted");
		return !mediaAccessGranted;
	});
	const [mediaAccessError, setMediaAccessError] = useState("");
	const [showAudioResumePopup, setShowAudioResumePopup] = useState(false);
	const [mediaStream, setMediaStream] = useState(null);
	const [textMessages, setTextMessages] = useState([]);
	const [networkType, setNetworkType] = useState("");
	const [previousNetworkCategory, setPreviousNetworkCategory] = useState("");
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const [audioContext, setAudioContext] = useState(null);

	// Network recovery state management
	const [isRecovering, setIsRecovering] = useState(false);
	const [recoveryAttempts, setRecoveryAttempts] = useState(0);
	const [lastRecoveryTime, setLastRecoveryTime] = useState(0);
	const maxRecoveryAttempts = 3;

	// Backend crash prevention state
	const [bitrateInterval, setBitrateInterval] = useState(null);
	const recoveryLockout = 60000; // 1 minute lockout
	const networkChangeDebounceTime = 5000; // 5 seconds
	let networkChangeTimeout = useRef(null);

	const timeoutRef = useRef(null);
	const mediaStreamRef = useRef(null); // Holds raw getUserMedia stream so initAgora can stop it before creating Agora tracks
	const isMobileDevice = useIsMobileDevice();

	// Audio activation timeout refs for cleanup
	const audioActivationTimeoutRef = useRef(null);
	const consultantAudioSetupTimeoutRef = useRef(null);
	const audioRetryTimeoutsRef = useRef([]);
	const isLeavingCallRef = useRef(false);

	// Function to stop all audio activation processes
	const stopAllAudioActivation = () => {
		logger.info('Stopping all audio activation processes');

		// NOTE: Do NOT set isLeavingCallRef.current here â€” that flag is ONLY for actual
		// leave/end call scenarios. Setting it here breaks socket event handlers
		// (OFF_AUDIO_TO_WEB_AST, ON_AUDIO_TO_WEB_AST) when useEffect cleanups run.

		// Clear main audio activation timeout
		if (audioActivationTimeoutRef.current) {
			clearTimeout(audioActivationTimeoutRef.current);
			audioActivationTimeoutRef.current = null;
		}

		// Clear consultant audio setup timeout
		if (consultantAudioSetupTimeoutRef.current) {
			clearTimeout(consultantAudioSetupTimeoutRef.current);
			consultantAudioSetupTimeoutRef.current = null;
		}

		// Clear all retry timeouts
		audioRetryTimeoutsRef.current.forEach((timeoutId, index) => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		});
		audioRetryTimeoutsRef.current = [];

		// Dismiss all toasts with audio-related keywords
		toast.dismiss();

		// Force dismiss any remaining audio toasters by their content
		setTimeout(() => {
			// This ensures any pending toasts are cleared
			toast.dismiss();
		}, 100);
		
	};	// Fix: Define handleOnlineStatus to prevent ReferenceError
	const handleOnlineStatus = () => {
		const isOnlineNow = navigator.onLine;
		handleConnectivityChange(isOnline, 'online_offline_event');
	};

	// Network and Audio Monitoring Functions 

	const checkNetworkType = () => {
		if ('connection' in navigator) {
			const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
			if (connection) {
				const effectiveType = connection.effectiveType || 'unknown';
				const connectionType = connection.type || 'unknown';

				// Detect WiFi vs Cellular
				let networkCategory = 'Unknown';
				if (connectionType === 'wifi') {
					networkCategory = 'WiFi';
				} else if (connectionType === 'cellular') {
					networkCategory = 'Cellular';
				} else if (connectionType === 'ethernet') {
					networkCategory = 'Ethernet';
				} else if (connectionType === 'bluetooth') {
					networkCategory = 'Bluetooth';
				} else {
					// Fallback: try to detect from effective type
					if (effectiveType === '4g' || effectiveType === '3g' || effectiveType === '2g') {
						networkCategory = effectiveType;
					} else if (effectiveType === 'slow-2g') {
						networkCategory = 'Cellular (Slow)';
					}
				}

				const networkInfo = {
					category: networkCategory,
					effectiveType: effectiveType,
					type: connectionType,
					downlink: connection.downlink || 'unknown',
					rtt: connection.rtt || 'unknown',
					saveData: connection.saveData || false
				};

				// Only log on significant network changes, not all checks
				logger.warn('Network Information:', { networkInfo });

				// toast.info(`Network: ${networkCategory} (${effectiveType})`);
				setNetworkType(`${networkCategory} - ${effectiveType}`);
				return networkInfo;
			}
		}
		logger.warn('Network API not supported', { reason: 'navigator.connection not available' });
		return { category: 'Unknown', effectiveType: 'unknown', type: 'unknown' };
	};

	// Enhanced network change handler with circuit breaker pattern
	const handleNetworkChange = () => {
		// Debounce network changes to prevent rapid-fire recovery attempts
		clearTimeout(networkChangeTimeout.current);
		networkChangeTimeout.current = setTimeout(() => {
			handleActualNetworkChange();
		}, networkChangeDebounceTime);
	};

	const handleActualNetworkChange = () => {
		const currentTime = Date.now();

		// Circuit breaker: Check if we're in recovery lockout
		if (currentTime - lastRecoveryTime < recoveryLockout && recoveryAttempts >= maxRecoveryAttempts) {
			logger.warn('Network recovery in lockout period', {
				attempts: recoveryAttempts,
				lockoutEnds: new Date(lastRecoveryTime + recoveryLockout).toISOString()
			});
			// toast.warning('Network recovery paused - too many attempts');
			return;
		}

		// Check if already recovering
		if (isRecovering) {
			logger.warn('Network recovery already in progress - skipping');
			return;
		}

		const previousCategory = previousNetworkCategory;
		const newNetworkInfo = checkNetworkType();

		// Update the current network type state
		setNetworkType(`${newNetworkInfo.category} - ${newNetworkInfo.effectiveType}`);
		setPreviousNetworkCategory(newNetworkInfo.category);

		// Check if connection is actually healthy before attempting recovery
		const isConnectionHealthy = () => {
			return agoraClient && agoraClient.connectionState === 'CONNECTED' &&
				socketRef.current && socketRef.current.connected;
		};

		if (isConnectionHealthy()) {
			logger.info('Network changed but connections are healthy - skipping recovery', {
				previousNetwork: previousCategory,
				newNetwork: newNetworkInfo.category
			});
			return;
		}

		// Get current audio state after network change - essential only
		const consultantAudio = document.getElementById("consultantAudio");

		// Essential network change data only
		const networkChangeData = {
			previousNetwork: previousCategory,
			newNetworkType: newNetworkInfo.category,
			newEffectiveType: newNetworkInfo.effectiveType,
			sinkId: consultantAudio ? (consultantAudio.sinkId || 'default') : 'no-audio-element',
			volume: consultantAudio ? Math.round(consultantAudio.volume * 100) : 0,
			audioElement: consultantAudio ? 'present' : 'missing'
		};

		logger.info('Network change requires recovery', { networkChangeData });

		/*
		// Show simplified messages based on network type with audio info
		const audioSummary = consultantAudio ?
			`Vol: ${Math.round(consultantAudio.volume * 100)}% | Sink: ${consultantAudio.sinkId || 'default'}` :
			'Audio: N/A';

		if (newNetworkInfo.category === 'Cellular') {
			const message = `Switched to ${newNetworkInfo.category} (${newNetworkInfo.effectiveType}) | ${audioSummary}`;
			toast.warning(message);
		} else if (newNetworkInfo.category === 'WiFi') {
			const message = `Connected to ${newNetworkInfo.category} (${newNetworkInfo.effectiveType}) | ${audioSummary}`;
			toast.success(message);
		} else {
			const message = `Network: ${newNetworkInfo.category} (${newNetworkInfo.effectiveType}) | ${audioSummary}`;
			toast.info(message);
		}

		*/
		// Perform controlled audio recovery with exponential backoff
		const performControlledRecovery = async () => {
			setIsRecovering(true);
			const currentAttempts = recoveryAttempts + 1;
			setRecoveryAttempts(currentAttempts);
			setLastRecoveryTime(currentTime);

			try {
				// Exponential backoff delay
				const backoffDelay = Math.min(1000 * Math.pow(2, currentAttempts - 1), 30000);
				await new Promise(resolve => setTimeout(resolve, backoffDelay));

				await performFullAudioRecovery('network_change_controlled', newNetworkInfo);

				// Reset attempts on success
				setRecoveryAttempts(0);
				// toast.success('Network recovery completed successfully');

			} catch (error) {
				logger.error('Controlled network recovery failed', {
					error: error.message,
					attempt: currentAttempts
				});

				// if (currentAttempts >= maxRecoveryAttempts) {
				// 	toast.error('Network recovery failed - manual refresh may be needed');
				// }
			} finally {
				setIsRecovering(false);
			}
		};

		// Only perform recovery for significant network changes
		if (previousCategory && previousCategory !== newNetworkInfo.category) {
			logger.warn('Significant network change detected - performing controlled recovery', {
				previousNetwork: previousCategory,
				newNetwork: newNetworkInfo.category,
				attempt: recoveryAttempts + 1
			});

			performControlledRecovery();
		}
	};

	// Universal network connectivity recovery - mirrors browser refresh behavior
	const performFullAudioRecovery = async (reason = 'unknown', networkInfo = null) => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping audio recovery - user is leaving call', { reason });
			return;
		}

		const startTime = Date.now();

		try {
			logger.info('Starting full audio recovery', {
				reason,
				networkInfo: networkInfo ? {
					category: networkInfo.category,
					effectiveType: networkInfo.effectiveType
				} : 'not_provided',
				timestamp: new Date().toISOString()
			});

			// Step 1: Reactivate all volume systems (both media and in-call)
			const activationResults = await activateAllVolumeSystems();
			logger.info('Volume systems reactivated during recovery', {
				mediaVolume: activationResults.mediaVolumeActivated,
				inCallVolume: activationResults.inCallVolumeActivated,
				communicationStream: activationResults.communicationStreamActivated
			});

			// Step 2: Check and resume audio context
			await checkAndResumeAudioContext();
			logger.info('Audio context checked/resumed during recovery');

			// Step 3: Setup consultant audio with retry mechanism (same as refresh)
			const audioSetupSuccess = await setupConsultantAudioWithRetry(5, 800); // Slightly faster for network recovery

			if (audioSetupSuccess) {
				logger.info('Consultant audio setup successful during recovery');
				// toast.success('Audio recovered after network change');
			} else {
				logger.warn('Consultant audio setup failed during recovery');
				// toast.warning('Audio recovery incomplete - may need manual refresh');
			}

			// Step 4: Ensure consultant audio element is properly configured (same as refresh)
			const consultantAudio = document.getElementById("consultantAudio");
			if (consultantAudio) {
				const wasUserSelfMuted = sessionStorage.getItem("wastSelfMuted") === "true";

				// Restore audio settings after network change
				const sinkSuccess = await safelySetAudioSink(consultantAudio);
				if (sinkSuccess) {
					logger.info('Audio sink restored during recovery', {
						sinkId: consultantAudio.sinkId || 'default',
						volume: Math.round(consultantAudio.volume * 100),
						userSelfMuted: wasUserSelfMuted
					});
				}

				// Preserve user's mute preference (same as refresh)
				if (!wasUserSelfMuted && consultantAudio.muted) {
					// Only unmute if user hasn't intentionally muted
					consultantAudio.muted = false;
					logger.info('Consultant audio unmuted after recovery');
				}
			}

			const recoveryDuration = Date.now() - startTime;
			logger.info('Full audio recovery completed', {
				reason,
				duration: `${recoveryDuration}ms`,
				success: true
			});

		} catch (error) {
			const recoveryDuration = Date.now() - startTime;
			logger.error('Full audio recovery failed', {
				reason,
				duration: `${recoveryDuration}ms`,
				error: error.message
			});

			// Fallback: try basic audio setup
			try {
				await setupConsultantAudioWithRetry(2, 1000); // Basic fallback
				// toast.warning('Basic audio recovery completed');
			} catch (fallbackError) {
				// toast.error('Audio recovery failed - manual refresh may be needed');
			}
		}
	};

	// Enhanced online/offline handler for mobile device support
	const handleConnectivityChange = (isOnline, source = 'unknown') => {
		const currentOnlineState = navigator.onLine;
		logger.warn('Connectivity status changed', {
			isOnline: currentOnlineState,
			previousState: isOnline,
			source,
			userAgent: navigator.userAgent.substring(0, 50) // First 50 chars for mobile detection
		});

		setIsOnline(currentOnlineState);

		if (currentOnlineState && !isOnline) {
			// Connection restored
			// toast.info('Connection restored - Recovering audio...');

			// Perform full audio recovery (same as browser refresh)
			setTimeout(() => {
				// Check if user is leaving call before recovery
				if (isLeavingCallRef.current) {
					logger.info('Audio recovery cancelled - user leaving call');
					return;
				}

				performFullAudioRecovery('connection_restored').then(() => {
					// Check again before showing success toast
					// if (!isLeavingCallRef.current) {
					// 	toast.success('Audio recovery completed after reconnection');
					// }
				}).catch((error) => {
					logger.error('Audio recovery failed after reconnection', {
						error: error.message
					});
					// Check before showing warning toast
					// if (!isLeavingCallRef.current) {
					// 	toast.warning('Audio recovery incomplete - check your connection');
					// }
				});
			}, 2000); // 2 second delay for connection stabilization

		} else if (!currentOnlineState) {
			// Connection lost
			toast.error('Connection lost - You are offline');
		}
	};

	// Mobile-specific network change detection (for devices without navigator.connection)
	const setupMobileNetworkDetection = () => {
		// Detect mobile devices
		const isMobile = /iPhone|iPad|iPod|Android|BlackBerry|Windows Phone/i.test(navigator.userAgent);
		let visibilityRecoveryTimeout = null;
		let focusRecoveryTimeout = null;
		let agoraMonitorStartTimeout = null;
		let agoraMonitorInterval = null;

		const handleVisibilityChange = () => {
			if (!document.hidden) {
				logger.info('App visibility restored - checking connectivity');
				visibilityRecoveryTimeout = setTimeout(() => {
					if (navigator.onLine) {
						performFullAudioRecovery('app_visibility_restored').catch((error) => {
							logger.error('Audio recovery failed on visibility change', { error: error.message });
						});
					}
				}, 1000);
			}
		};

		const handleMobileFocus = () => {
			logger.info('Window focus gained - checking audio state');
			focusRecoveryTimeout = setTimeout(() => {
				const consultantAudio = document.getElementById("consultantAudio");
				if (consultantAudio && navigator.onLine) {
					checkAndResumeAudioContext().then(() => {
						if (consultantAudio.paused || consultantAudio.volume === 0) {
							logger.warn('Audio issues detected on focus - performing recovery');
							performFullAudioRecovery('window_focus_recovery');
						}
					});
				}
			}, 500);
		};

		if (isMobile) {
			logger.info('Mobile device detected - setting up enhanced network monitoring', {
				userAgent: navigator.userAgent.substring(0, 100),
				hasConnectionAPI: !!navigator.connection
			});

			// Method 1: Visibility API (app backgrounding/foregrounding)
			if (typeof document.hidden !== "undefined") {
				document.addEventListener('visibilitychange', handleVisibilityChange);
			}

			// Method 2: Page focus events (additional mobile support)
			window.addEventListener('focus', handleMobileFocus);

			// Method 3: Agora connection monitoring (works on all devices)
			const monitorAgoraConnection = () => {
				if (agoraClient) {
					agoraMonitorInterval = setInterval(() => {
						try {
							if (!navigator.onLine) {
								clearInterval(agoraMonitorInterval);
								return;
							}

							// Skip audio recovery while Agora is not fully connected — creating a new
							// AudioContext during a reconnect cycle produces spurious DISCONNECTED→CONNECTING
							// logs and wastes recovery effort before the channel is even back up.
							if (!agoraClient || agoraClient.connectionState !== "CONNECTED") {
								return;
							}

							const consultantAudio = document.getElementById("consultantAudio");
							if (consultantAudio && (consultantAudio.paused || consultantAudio.volume === 0)) {
								logger.warn('Agora monitoring detected audio issues');
								performFullAudioRecovery('agora_monitoring');
								clearInterval(agoraMonitorInterval);
							}
						} catch (error) {
							logger.error('Error in Agora monitoring', { error: error.message });
							clearInterval(agoraMonitorInterval);
						}
					}, 10000);

					setTimeout(() => clearInterval(agoraMonitorInterval), 300000);
				}
			};

			// Start Agora monitoring
			agoraMonitorStartTimeout = setTimeout(monitorAgoraConnection, 5000);
		}

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('focus', handleMobileFocus);
			clearTimeout(visibilityRecoveryTimeout);
			clearTimeout(focusRecoveryTimeout);
			clearTimeout(agoraMonitorStartTimeout);
			clearInterval(agoraMonitorInterval);
		};
	};


	const handleAudioSinkChange = async () => {
		const audioElement = document.getElementById("consultantAudio");
		// Get current sink ID before change
		const currentSinkId = audioElement?.sinkId || 'default';
		logger.info('Audio sink chan', {
			currentSinkId,
			elementId: 'consultantAudio',
			hasSetSinkId: typeof audioElement?.setSinkId === 'function'
		});

		// toast.info('handleAudioSinkChange:', audioElement);
		if (audioElement) {
			// toast.info('handleAudioSinkChange:', audioElement);
			// Set default sink without affecting current mute status 
			const currentMuteStatus = audioElement.muted;
			const sinkSetSuccessfully = await safelySetAudioSink(audioElement);
			if (sinkSetSuccessfully) {
				// Preserve the mute status after setting sink 
				audioElement.muted = currentMuteStatus;
				logger.info('Audio sink updated successfully', {
					previousSinkId: currentSinkId,
					newSinkId: audioElement.sinkId || 'default',
					muteStatus: currentMuteStatus,
					elementId: 'consultantAudio'
				});
			} else {
				// toast.error('Failed to set audio output device');
				logger.error('Failed to set audio output device', {
					currentSinkId,
					elementId: 'consultantAudio'
				});
			}
		}
	};

	const checkAndResumeAudioContext = async () => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping audio context check - user is leaving call');
			return;
		}

		if (!audioContext) {
			// toast.info('No audio context found - initializing...');
			initializeAudioContext();
			return;
		}

		if (audioContext.state === 'closed') {
			logger.info('Audio context was closed, reinitializing', {
				previousState: 'closed',
				action: 'reinitialize'
			});
			// toast.warning('Audio context was closed - reinitializing...');
			initializeAudioContext();
			return;
		}

		if (audioContext.state === 'suspended') {
			// toast.warning('Audio context suspended - resuming...');
			try {
				await audioContext.resume();
				logger.info('Audio context resumed successfully', {
					previousState: 'suspended',
					newState: audioContext.state
				});
				// toast.success('Audio context resumed successfully!');
			} catch (error) {
				logger.error('Failed to resume audio context', { error: error.message });
				// toast.error('Failed to resume audio context - reinitializing...');
				initializeAudioContext();
			}
		} else if (audioContext.state === 'running') {
			logger.info('Audio context already running', {
				currentState: audioContext.state
			});
			// Only show this occasionally to avoid spam
			// if (Math.random() < 0.1) { // 10% chance to show this message
			// 	toast.success('Audio context already running');
			// }
		}
	};

	const safelySetAudioSink = async (audioElement) => {
		const previousSinkId = audioElement.sinkId || 'default';
		logger.info('Attempting to set audio sink', {
			elementId: audioElement.id || 'unknown',
			previousSinkId,
			targetSinkId: 'default',
			hasSetSinkId: typeof audioElement.setSinkId === 'function'
		});

		// Enhanced audio sink setting - try speaker device first, then default
		const sinkSuccess = await safelySetAudioSinkToSpeaker(audioElement);
		if (sinkSuccess) {
			logger.info('Audio sink successfully changed', {
				elementId: audioElement.id || 'unknown',
				previousSinkId,
				newSinkId: audioElement.sinkId || 'default',
				success: true
			});
			return true;
		}

		// Fallback to original logic for compatibility
		if (typeof audioElement.setSinkId === 'function') {
			try {
				await audioElement.setSinkId('default');
				const newSinkId = audioElement.sinkId || 'default';
				logger.info('Audio sink successfully changed (fallback)', {
					elementId: audioElement.id || 'unknown',
					previousSinkId,
					newSinkId,
					success: true
				});
				return true;
			} catch (error) {
				console.error('Failed to set audio sink for consultant audio:', error);
				logger.error('Failed to set audio sink', {
					elementId: audioElement.id || 'unknown',
					previousSinkId,
					targetSinkId: 'default',
					error: error.message,
					errorName: error.name
				});
				return false;
			}
		} else {
			logger.warn('setSinkId not supported', {
				elementId: audioElement.id || 'unknown',
				currentSinkId: previousSinkId,
				userAgent: navigator.userAgent
			});
		}
		return false;
	};

	const handleAudioStateChange = async () => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping audio state change handling - user is leaving call');
			return;
		}

		const audioElement = document.getElementById("consultantAudio");

		if (audioElement) {
			const audioState = {
				paused: audioElement.paused,
				muted: audioElement.muted,
				volume: audioElement.volume,
				sinkId: audioElement.sinkId || 'default',
				currentTime: audioElement.currentTime,
				duration: audioElement.duration || 0,
				readyState: audioElement.readyState,
				networkState: audioElement.networkState,
				hasSetSinkId: typeof audioElement.setSinkId === 'function'
			};

			logger.info('Audio state changed', {
				elementId: 'consultantAudio',
				audioState,
				eventType: 'state_change'
			});

			// Check if audio context needs to be resumed (but don't affect user mute status) 
			await checkAndResumeAudioContext();

			// Log audio state without changing user's mute preference 
			if (audioElement.paused) {
				// toast.warning('Consultant audio is paused');
				logger.warn('Consultant audio paused', {
					sinkId: audioState.sinkId,
					elementId: 'consultantAudio'
				});
			} else {
				logger.info('Consultant audio playing', {
					sinkId: audioState.sinkId,
					elementId: 'consultantAudio',
					volume: audioState.volume
				});
			}
			// Only show mute warning if it's not intentionally muted by user or system 
			if (audioElement.muted) {
				logger.info('Consultant audio muted', {
					sinkId: audioState.sinkId,
					elementId: 'consultantAudio',
					intentional: true
				});
				// Don't show toast for mute status as it might be intentional 
			}
		}
	};

	const initializeAudioContext = () => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping audio context initialization - user is leaving call');
			return;
		}

		try {
			// For browser refresh scenarios, always ensure we have a working audio context
			const isPageRefresh = !state && sessionStorage.getItem("WastSessionId");

			if (isPageRefresh) {
				logger.info('Page refresh detected - ensuring fresh audio context', {
					currentContextState: audioContext?.state || 'none',
					reason: 'refresh_needs_working_context'
				});
				// toast.info('Page refresh detected - initializing audio...');

				// On refresh, if we don't have a context or it's not running, create a new one
				if (!audioContext || audioContext.state !== 'running') {
					// toast.info('Creating fresh audio context for refresh...');
					createNewAudioContext();
					return;
				}
			}

			// Don't close existing working audio context - preserve it for normal operation
			if (audioContext && audioContext.state === 'running') {
				logger.info('Audio context already running - preserving existing context', {
					currentState: audioContext.state,
					reason: 'avoid_disruption_on_normal_operation'
				});
				// toast.success('Audio context already running - preserved');
				return; // Keep the existing working context
			}

			// Only close if the context is in a problematic state
			if (audioContext && audioContext.state === 'suspended') {
				logger.info('Attempting to resume existing suspended audio context', {
					currentState: audioContext.state
				});
				// toast.warning('Audio context suspended - attempting to resume...');

				audioContext.resume().then(() => {
					logger.info('Successfully resumed existing audio context');
					// toast.success('Audio context resumed successfully!');
				}).catch((error) => {
					logger.warn('Failed to resume audio context, creating new one', { error: error.message });
					// toast.error('Failed to resume - creating new audio context...');
					createNewAudioContext();
				});
				return;
			}

			// Only close if the context is closed or we don't have one
			if (audioContext && audioContext.state === 'closed') {
				logger.info('Audio context is closed, creating new one', {
					currentState: 'closed'
				});
				// toast.warning('Audio context closed - creating new one...');
			} else if (!audioContext) {
				// toast.info('Initializing audio context for first time...');
			}

			createNewAudioContext();

		} catch (error) {
			logger.error('Failed to initialize audio context', { error: error.message });
			// toast.error('Audio context initialization failed - retrying...');
			createNewAudioContext();
		}
	};	// Helper function to create a new audio context
	const createNewAudioContext = () => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping audio context creation - user is leaving call');
			return;
		}

		try {
			const AudioContext = window.AudioContext || window.webkitAudioContext;
			if (AudioContext) {
				const ctx = new AudioContext();
				setAudioContext(ctx);
				/*
				// Check if this is a page refresh scenario
				const isPageRefresh = !state && sessionStorage.getItem("WastSessionId");

				// Show immediate toast based on initial state
				if (ctx.state === 'running') {
					toast.success('ðŸŽµ New audio context created and running!');
				} else if (ctx.state === 'suspended') {
					if (isPageRefresh) {
						toast.warning('ðŸ”‡ Audio context suspended due to browser refresh - auto-resuming...');
					} else {
						toast.warning('ðŸ”‡ New audio context created but suspended - will auto-resume');
					}
				} else {
					toast.info(`ðŸŽ§ New audio context created (state: ${ctx.state})`);
				}
				*/
				ctx.addEventListener('statechange', () => {

					// Log audio context state changes for debugging
					logger.info('Audio context state changed', {
						newState: ctx.state,
						timestamp: new Date().toISOString()
					});

					// // Enhanced toast notifications for all audio context state changes
					// if (ctx.state === 'suspended') {
					// 	toast.warning('ðŸ”‡ Audio context suspended - tap to resume');
					// 	logger.warn('Audio context suspended - user interaction needed');
					// } else if (ctx.state === 'running') {
					// 	toast.success('ðŸ”Š Audio context running - ready for sound');
					// 	logger.info('Audio context running successfully');
					// } else if (ctx.state === 'closed') {
					// 	toast.error('âŒ Audio context closed - will reinitialize');
					// 	logger.error('Audio context closed unexpectedly');
					// } else {
					// 	toast.info(`ðŸŽµ Audio context state: ${ctx.state}`);
					// 	logger.info(`Audio context in state: ${ctx.state}`);
					// }
				});

				// Try to resume the context immediately if it starts suspended
				if (ctx.state === 'suspended') {
					const isRefresh = !state && sessionStorage.getItem("WastSessionId");

					if (isRefresh) {
						// toast.info('Auto-resuming audio context for page refresh...');
						// For page refresh, try multiple resume attempts with different strategies
						let resumeAttempts = 0;
						const maxAttempts = 3;

						const attemptResume = () => {
							// Check if user is leaving call before each resume attempt
							if (isLeavingCallRef.current) {
								logger.info('Auto-resume attempt cancelled - user leaving call');
								return;
							}

							resumeAttempts++;
							logger.info(`Attempting to resume audio context (attempt ${resumeAttempts}/${maxAttempts}) for page refresh`);

							ctx.resume().then(() => {
								logger.info(`Audio context auto-resumed on attempt ${resumeAttempts} after page refresh`);
								// toast.success(`Audio context resumed successfully! (attempt ${resumeAttempts})`);
							}).catch((error) => {
								logger.error(`Failed to auto-resume audio context on attempt ${resumeAttempts}`, { error: error.message });

								// Check if user is leaving call before showing retry toasts
								if (isLeavingCallRef.current) {
									logger.info('Auto-resume attempts cancelled - user leaving call');
									return;
								}

								if (resumeAttempts < maxAttempts) {
									// toast.warning(`Resume attempt ${resumeAttempts} failed - retrying in ${500 * resumeAttempts}ms...`);
									setTimeout(() => {
										// Check again before recursive retry
										if (!isLeavingCallRef.current) {
											attemptResume();
										}
									}, 500 * resumeAttempts); // Increasing delay: 500ms, 1s, 1.5s
								} else {
									// toast.error('All auto-resume attempts failed - manual activation needed');
									logger.warn('All auto-resume attempts failed for page refresh scenario');
								}
							});
						};

						// Start first attempt after a brief delay to ensure context is fully initialized
						setTimeout(() => {
							// Check if user is leaving call before starting attempts
							if (!isLeavingCallRef.current) {
								attemptResume();
							}
						}, 200);
					} else {
						// Normal first-time load - single attempt
						// toast.info('Auto-resuming suspended audio context...');
						ctx.resume().then(() => {
							// Check if user is leaving call before success toast
							if (!isLeavingCallRef.current) {
								logger.info('Audio context auto-resumed on initialization');
								// toast.success('Audio context auto-resumed successfully!');
							}
						}).catch((error) => {
							logger.error('Failed to auto-resume audio context', { error: error.message });
							// Check if user is leaving call before error toast
							if (!isLeavingCallRef.current) {
								// toast.error('Failed to auto-resume - manual activation needed');
							}
						});
					}
				}
			} else {
				// toast.error('AudioContext not supported in this browser');
				logger.error('AudioContext not available');
			}
		} catch (error) {
			logger.error('Failed to create new audio context', { error: error.message });
			// toast.error('Failed to create audio context: ' + error.message);
		}
	};

	// Get detailed network information
	const getNetworkInfo = () => {
		if ('connection' in navigator) {
			const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
			if (connection) {
				return {
					isWiFi: connection.type === 'wifi',
					isCellular: connection.type === 'cellular',
					isEthernet: connection.type === 'ethernet',
					connectionType: connection.type || 'unknown',
					effectiveType: connection.effectiveType || 'unknown',
					downlink: connection.downlink || null, // Download speed in Mbps
					rtt: connection.rtt || null, // Round trip time in ms
					saveData: connection.saveData || false, // Data saver mode
					online: navigator.onLine
				};
			}
		}
		return {
			isWiFi: false,
			isCellular: false,
			isEthernet: false,
			connectionType: 'unknown',
			effectiveType: 'unknown',
			downlink: null,
			rtt: null,
			saveData: false,
			online: navigator.onLine
		};
	};

	// Check if user is on cellular and warn about data usage
	const checkDataUsageWarning = () => {
		const networkInfo = getNetworkInfo();
		if (networkInfo.isCellular) {
			logger.warn('User on cellular network - high data usage expected', { networkInfo });
			// toast.warning('You are using cellular data. Video calls consume significant data.');
			return true;
		}
		return false;
	};

	// Display current network status (can be called anytime)
	const showNetworkStatus = () => {
		const networkInfo = getNetworkInfo();
		let statusMessage = `Network: ${networkInfo.connectionType} (${networkInfo.effectiveType})`;

		if (networkInfo.downlink) {
			statusMessage += ` | Speed: ${networkInfo.downlink} Mbps`;
		}
		if (networkInfo.rtt) {
			statusMessage += ` | Latency: ${networkInfo.rtt}ms`;
		}
		if (networkInfo.saveData) {
			statusMessage += ` | Data Saver: ON`;
		}

		logger.info('Network status check', { networkInfo });
		// toast.info(statusMessage);

		return networkInfo;
	};

	// Log device capability counts without recording stable IDs or hardware labels.
	const logAvailableMediaDevices = async () => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const deviceInfo = {
				audioInputs: [],
				audioOutputs: [],
				videoInputs: [],
				total: devices.length
			};

			devices.forEach((device, index) => {
				const deviceDetails = {
					index,
					kind: device.kind,
					hasLabel: !!device.label,
					isDefault: device.deviceId === "default",
					isCommunications: device.deviceId === "communications"
				};

				if (device.kind === 'audioinput') {
					deviceInfo.audioInputs.push(deviceDetails);
				} else if (device.kind === 'audiooutput') {
					deviceInfo.audioOutputs.push(deviceDetails);
				} else if (device.kind === 'videoinput') {
					deviceInfo.videoInputs.push(deviceDetails);
				}
			});

			logger.info('Available media devices enumerated', {
				deviceInfo,
				audioInputCount: deviceInfo.audioInputs.length,
				audioOutputCount: deviceInfo.audioOutputs.length,
				videoInputCount: deviceInfo.videoInputs.length,
				totalDevices: deviceInfo.total
			});
		return deviceInfo;
		} catch (error) {
			logger.error('Failed to enumerate media devices', {
				error: error.message,
				errorName: error.name
			});
			console.error('Failed to enumerate media devices:', error);
			return null;
		}
	};

	// Log current audio element sink information
	const logCurrentAudioSinks = () => {
		const consultantAudio = document.getElementById("consultantAudio");
		const audioElements = document.querySelectorAll('audio');
		const videoElements = document.querySelectorAll('video');

		const sinkInfo = {
			consultantAudio: consultantAudio ? {
				id: consultantAudio.id,
				sinkId: consultantAudio.sinkId || 'default',
				muted: consultantAudio.muted,
				volume: consultantAudio.volume,
				paused: consultantAudio.paused,
				hasSetSinkId: typeof consultantAudio.setSinkId === 'function'
			} : null,
			allAudioElements: Array.from(audioElements).map(audio => ({
				id: audio.id || 'unnamed',
				sinkId: audio.sinkId || 'default',
				muted: audio.muted,
				volume: audio.volume,
				paused: audio.paused,
				src: audio.src || 'stream',
				hasSetSinkId: typeof audio.setSinkId === 'function'
			})),
			allVideoElements: Array.from(videoElements).map(video => ({
				id: video.id || 'unnamed',
				muted: video.muted,
				volume: video.volume,
				paused: video.paused,
				videoWidth: video.videoWidth,
				videoHeight: video.videoHeight,
				src: video.src || 'stream'
			}))
		};

		logger.info('Current audio/video sink information', {
			sinkInfo,
			audioElementCount: audioElements.length,
			videoElementCount: videoElements.length
		});

		return sinkInfo;
	};

	const onJoinCall = () => {
		// Activate all volume systems (both media and in-call) immediately on join
		activateAllVolumeSystems();

		// Log essential info on join call (including refresh scenarios)
		const networkInfo = getNetworkInfo();
		const consultantAudio = document.getElementById("consultantAudio");

		// Detect if this is a refresh scenario or genuine join
		const isRefreshCall = !state && sessionStorage.getItem("WastSessionId");

		logger.warn('Join Call', {
			networkType: networkInfo.connectionType || 'unknown',
			effectiveType: networkInfo.effectiveType || 'unknown',
			networkCategory: networkInfo.isWiFi ? 'WiFi' : networkInfo.isCellular ? 'Cellular' : 'Unknown',
			sinkId: consultantAudio ? (consultantAudio.sinkId || 'default') : 'no-audio-element',
			volume: consultantAudio ? Math.round(consultantAudio.volume * 100) : 0,
			audioElement: consultantAudio ? 'present' : 'missing',
			webAssistantName: sessionStorage.getItem("webAssistantName"),
			isRefreshCall
		});

		const SessionId = sessionStorage.getItem("WastSessionId");
		let callSession1 = JSON.parse(SessionId);
		if (!socketRef.current && apis.socketURL) {
			socketRef.current = io(apis.socketURL);
		}

		// Re-register the WAST -> room socket mapping on every (re)connect.
		// The server routes CONSULTANT_END_CALL / ROOM_DESTROYED to a stored socket id
		// (call[socketId]["webAssistant"]) which is only written on WEB_AST_CALL_INITIATION.
		// iOS Safari drops the websocket when the tab is backgrounded/idle; socket.io then
		// auto-reconnects with a NEW socket.id, leaving that mapping pointing at a dead socket,
		// so the end-call event never reaches this client (Android keeps the socket alive, so it works there).
		// Re-emitting on 'connect' (which fires on initial connect AND every reconnect) keeps the
		// mapping fresh. Bind once per socket instance to avoid duplicate handlers.
		if (socketRef.current && !socketRef.current._wastReRegisterBound) {
			socketRef.current._wastReRegisterBound = true;
			socketRef.current.on("connect", () => {
				if (isLeavingCallRef.current) return;
				const storedSession = sessionStorage.getItem("WastSessionId");
				if (!storedSession) return;
				logger.warn('Re-registering WAST with room on socket (re)connect', {
					socketId: socketRef.current?.id,
				});
				// Lightweight re-register: refreshes only the server's socket-id mapping,
				// without re-running the full initiation flow (WAST_CALL_TRANSFERRED / DB / JOIN_ROOM).
				socketRef.current?.emit(WEB_AST_RECONNECT, {
					sessionId: JSON.parse(storedSession),
				});
			});
		}

		socketRef.current?.emit(WEB_AST_CALL_INITIATION, {
			webAssistantName: sessionStorage.getItem("webAssistantName"),
			sessionId: callSession1,
		});
	};

	const RejoinWast = () => {
		// Mark as leaving before cleanup
		isLeavingCallRef.current = true;
		stopAllAudioActivation();

		// Log essential info on rejoin
		const networkInfo = getNetworkInfo();
		const consultantAudio = document.getElementById("consultantAudio");

		logger.warn('Rejoin Call', {
			networkType: networkInfo.connectionType || 'unknown',
			effectiveType: networkInfo.effectiveType || 'unknown',
			networkCategory: networkInfo.isWiFi ? 'WiFi' : networkInfo.isCellular ? 'Cellular' : 'Unknown',
			sinkId: consultantAudio ? (consultantAudio.sinkId || 'default') : 'no-audio-element',
			volume: consultantAudio ? Math.round(consultantAudio.volume * 100) : 0,
			audioElement: consultantAudio ? 'present' : 'missing'
		});

		const SessionId = sessionStorage.getItem("WastSessionId");
		let callSession = JSON.parse(SessionId);

		// Clear audio test completion to ensure popup shows on next join
		sessionStorage.removeItem('audioTestCompleted');

		navigate("/webAssistantJoin/" + callSession);
	};

	const executeAudioSetupAfterMediaAccess = async () => {
		logger.logUserAction('media_volume_confirmed', { timestamp: new Date().toISOString() });
		sessionStorage.setItem("volumeConfirmed", "true");

		try {
			// Step 1: Reactivate all volume systems (both media and in-call)
			await activateAllVolumeSystems();

			// Step 2: Check and resume audio context
			await checkAndResumeAudioContext();

			// Step 3: Setup consultant audio element with retry mechanism
			await setupConsultantAudioWithRetry();

			logger.info('Volume confirmed - triggering Agora initialization sequence', {
				hasWebAsstRoomDetails: !!webAsstRoomDetails?.roomId,
				isPluginAttached
			});

			if (isPluginAttached) {
				await setupConsultantAudioWithRetry();
			}

			// This ensures that even on stable WiFi, audio elements are properly refreshed
			const currentNetworkInfo = getNetworkInfo();
			logger.info('Forcing audio refresh after volume confirmation', {
				networkType: currentNetworkInfo.connectionType,
				isWiFi: currentNetworkInfo.isWiFi,
				reason: 'media_volume_confirmed'
			});

			// Perform audio refresh even on WiFi to ensure proper audio setup
			setTimeout(async () => {
				try {
					await performFullAudioRecovery('media_volume_confirmed', currentNetworkInfo);
				} catch (error) {
					logger.error('Audio refresh failed after volume confirmation', {
						error: error.message,
						networkType: currentNetworkInfo.connectionType
					});
				}
			}, 1000);

		} catch (error) {
			logger.error('Error during volume confirmation sequence', {
				error: error.message
			});
		}
	};

	// Enhanced audio setup function with retry mechanism
	const setupConsultantAudioWithRetry = async (maxRetries = 10, delayMs = 500) => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping consultant audio setup - user is leaving call');
			return false;
		}

		let attempts = 0;

		while (attempts < maxRetries) {
			// Check again inside the loop in case user leaves during retry
			if (isLeavingCallRef.current) {
				logger.info('Stopping consultant audio setup - user is leaving call');
				return false;
			}

			attempts++;

			try {

				// Force media volume activation for this specific element
				await activateMediaVolumeForElement();

				const consultantAudio = document.getElementById("consultantAudio");

				if (!consultantAudio) {
					logger.warn(`Setup attempt ${attempts}/${maxRetries}: Consultant audio element not found`, {
						attempt: attempts,
						delayMs
					});

					if (attempts < maxRetries) {
						await new Promise(resolve => setTimeout(resolve, delayMs));
						continue;
					} else {
						throw new Error('Consultant audio element not found after all retries');
					}
				}

				// Ensure audio context is running
				await checkAndResumeAudioContext();

				// Set volume and unmute (if user hasn't self-muted)
				const userSelfMuted = sessionStorage.getItem("wastSelfMuted") === "true";
				consultantAudio.volume = 1.0;

				if (!userSelfMuted) {
					consultantAudio.muted = false;
				}

				// Try to set audio sink to speaker device
				const sinkSetSuccess = await safelySetAudioSinkToSpeaker(consultantAudio);

				logger.info(`Audio setup successful on attempt ${attempts}`, {
					attempt: attempts,
					volume: consultantAudio.volume,
					muted: consultantAudio.muted,
					sinkId: consultantAudio.sinkId || 'default',
					sinkSetSuccess,
					userSelfMuted
				});
				return true;

			} catch (error) {
				logger.error(`Audio setup failed on attempt ${attempts}`, {
					attempt: attempts,
					maxRetries,
					error: error.message
				});

				if (attempts < maxRetries) {
					await new Promise(resolve => setTimeout(resolve, delayMs));
				} else {
					return false;
				}
			}
		}

		return false;
	};

	// Element-specific media volume activation
	const activateMediaVolumeForElement = async () => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping media volume activation for element - user is leaving call');
			return false;
		}

		const startTime = Date.now();

		try {
			logger.info('Activating media volume for consultant audio element', {
				timestamp: new Date().toISOString(),
				purpose: 'force_media_volume_for_consultant_audio'
			});

			// Create a dedicated audio element for volume activation
			const activationAudio = document.createElement('audio');
			activationAudio.id = 'consultant-audio-activator';
			activationAudio.style.position = 'absolute';
			activationAudio.style.top = '-9999px';
			activationAudio.style.left = '-9999px';
			activationAudio.style.width = '1px';
			activationAudio.style.height = '1px';
			activationAudio.style.opacity = '0';
			activationAudio.style.pointerEvents = 'none';
			activationAudio.style.zIndex = '-1000';
			activationAudio.muted = false;
			activationAudio.volume = 0.01; // 1% volume
			activationAudio.autoplay = true;
			activationAudio.loop = false;
			activationAudio.preload = 'auto';

			// Generate synthetic audio for media volume activation
			const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
			const oscillator = audioCtx.createOscillator();
			const gainNode = audioCtx.createGain();
			const destination = audioCtx.createMediaStreamDestination();

			oscillator.connect(gainNode);
			gainNode.connect(destination);

			// Use 60Hz frequency (slightly higher than 40Hz for better compatibility)
			oscillator.frequency.setValueAtTime(60, audioCtx.currentTime);
			gainNode.gain.setValueAtTime(0.02, audioCtx.currentTime); // 2% gain

			activationAudio.srcObject = destination.stream;
			document.body.appendChild(activationAudio);

			return new Promise((resolve) => {
				const cleanup = () => {
					try {
						if (oscillator) oscillator.stop();
						if (audioCtx && audioCtx.state !== 'closed') audioCtx.close();
						if (destination && destination.stream) {
							destination.stream.getTracks().forEach(track => track.stop());
						}
						if (activationAudio.parentNode) {
							activationAudio.parentNode.removeChild(activationAudio);
						}

						logger.info('Element-specific media activation completed', {
							duration: `${Date.now() - startTime}ms`,
							success: true
						});

						resolve(true);
					} catch (cleanupError) {
						logger.error('Error during element activation cleanup', {
							error: cleanupError.message
						});
						resolve(false);
					}
				};

				// Start oscillator
				oscillator.start();
				oscillator.stop(audioCtx.currentTime + 1.0); // Play for 1 second

				activationAudio.addEventListener('playing', () => {
					logger.info('Element activation audio started playing');
					setTimeout(cleanup, 800); // Cleanup after 800ms
				});

				activationAudio.addEventListener('error', cleanup);

				// Play the audio
				activationAudio.play().catch((error) => {
					logger.error('Failed to play element activation audio', {
						error: error.message
					});
					cleanup();
				});

				// Fallback cleanup
				setTimeout(cleanup, 1500);
			});

		} catch (error) {
			logger.error('Element-specific media activation failed', {
				error: error.message,
				duration: `${Date.now() - startTime}ms`
			});
			return false;
		}
	};

	// Enhanced audio sink setting with speaker device preference
	const safelySetAudioSinkToSpeaker = async (audioElement) => {
		if (!audioElement || typeof audioElement.setSinkId !== 'function') {
			logger.warn('setSinkId not supported', {
				hasElement: !!audioElement,
				hasSetSinkId: !!(audioElement && typeof audioElement.setSinkId === 'function')
			});
			return false;
		}

		try {
			// Get available audio output devices
			const devices = await navigator.mediaDevices.enumerateDevices();
			const audioOutputs = devices.filter(device => device.kind === 'audiooutput');

			logger.info('Available audio output devices', {
				count: audioOutputs.length,
				devices: audioOutputs.map(d => ({
					hasLabel: !!d.label,
					isDefault: d.deviceId === 'default',
					isCommunications: d.deviceId === 'communications'
				}))
			});

			// On Windows the OS exposes a dedicated 'communications' device (deviceId='communications')
			// that routes to whatever the user set as their default call audio output.
			// Prefer that first, then fall back to label matching which covers Mac/Linux speakers.
			// Explicitly exclude HDMI/monitor audio which is common in US office setups but
			// produces silence when the monitor has no built-in speakers.
			const commDevice = audioOutputs.find(d => d.deviceId === 'communications');
			const speakerDevice = commDevice || audioOutputs.find(device => {
				if (!device.label) return false;
				const label = device.label.toLowerCase();
				if (label.includes('hdmi') || label.includes('displayport') || label.includes('display audio')) return false;
				return (
					label.includes('speaker') ||
					label.includes('built-in') ||
					label.includes('internal') ||
					label.includes('realtek') ||
					label.includes('audio output') ||
					label.includes('headphone') ||
					label.includes('headset')
				);
			});

			let targetDeviceId = 'default';
			if (speakerDevice) {
				targetDeviceId = speakerDevice.deviceId;
				logger.info('Found audio output device', {
					route: speakerDevice.deviceId === 'communications' ? 'communications' : 'selected',
					hasLabel: !!speakerDevice.label
				});
			}

			// Set the audio sink
			const previousSinkId = audioElement.sinkId || 'default';
			await audioElement.setSinkId(targetDeviceId);

			logger.info('Audio sink set successfully', {
				elementId: audioElement.id || 'unknown',
				previousSinkId,
				newSinkId: audioElement.sinkId || 'default',
				targetWasSpeaker: !!speakerDevice
			});

			return true;

		} catch (error) {
			logger.error('Failed to set audio sink to speaker', {
				elementId: audioElement.id || 'unknown',
				error: error.message,
				errorName: error.name
			});

			// Fallback to default device
			try {
				await audioElement.setSinkId('default');
				logger.info('Fallback to default audio sink successful');
				return true;
			} catch (fallbackError) {
				logger.error('Fallback to default sink also failed', {
					error: fallbackError.message
				});
				return false;
			}
		}
	};

	// Define the handler outside useEffect using useCallback
	const handleDrawText = useCallback((data) => {
		const id = `${Date.now()}-${Math.random()}`;
		const newMessage = { id, text: data };

		setTextMessages((prev) => [...prev, newMessage]);

		setTimeout(() => {
			setTextMessages((prev) => prev.filter((msg) => msg.id !== id));
		}, 10000);
	}, []);

	const handleClose = (id) => {
		setTextMessages((prev) => prev.filter((msg) => msg.id !== id));
	};

	// Enhanced audio-based media volume activation (primary method)
	const tryAudioActivation = async (startTime) => {
		return new Promise((resolve) => {
			// Check if user is leaving call
			if (isLeavingCallRef.current) {
				logger.info('Skipping audio activation - user is leaving call');
				resolve(false);
				return;
			}

			try {
				logger.info('Starting enhanced audio-based media volume activation', {
					timestamp: new Date().toISOString(),
					method: 'enhanced_audio_element_approach',
					purpose: 'primary_media_volume_activation'
				});

				// Create hidden audio element
				const hiddenAudio = document.createElement('audio');
				hiddenAudio.id = 'background-audio-activator';
				hiddenAudio.style.position = 'absolute';
				hiddenAudio.style.top = '-9999px';
				hiddenAudio.style.left = '-9999px';
				hiddenAudio.style.width = '1px';
				hiddenAudio.style.height = '1px';
				hiddenAudio.style.opacity = '0';
				hiddenAudio.style.pointerEvents = 'none';
				hiddenAudio.style.zIndex = '-1000';
				hiddenAudio.muted = false; // Critical: NOT muted to activate media volume
				hiddenAudio.volume = 0.01; // Low but audible volume (1%)
				hiddenAudio.autoplay = true;
				hiddenAudio.loop = false;
				hiddenAudio.preload = 'auto';

				// Create audio context and generate a brief, low-frequency tone
				const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
				const oscillator = audioCtx.createOscillator();
				const gainNode = audioCtx.createGain();
				const destination = audioCtx.createMediaStreamDestination();

				// Connect audio nodes
				oscillator.connect(gainNode);
				gainNode.connect(destination);

				// Configure low frequency and volume for better compatibility
				oscillator.frequency.setValueAtTime(60, audioCtx.currentTime); // 60Hz (low but audible)
				gainNode.gain.setValueAtTime(0.02, audioCtx.currentTime); // 2% gain

				// Set the generated stream as audio source
				hiddenAudio.srcObject = destination.stream;

				// Add to DOM
				document.body.appendChild(hiddenAudio);

				logger.info('Enhanced audio activation setup complete', {
					audioId: hiddenAudio.id,
					volume: hiddenAudio.volume,
					frequency: '60Hz',
					gain: '2%',
					muted: hiddenAudio.muted,
					streamActive: destination.stream.active,
					method: 'primary_audio_activation'
				});

				const cleanup = () => {
					try {
						// Stop oscillator
						if (oscillator) {
							oscillator.stop();
						}

						// Close audio context
						if (audioCtx && audioCtx.state !== 'closed') {
							audioCtx.close();
						}

						// Stop stream tracks
						if (destination && destination.stream) {
							destination.stream.getTracks().forEach(track => {
								track.stop();
								logger.info('Audio track stopped', { trackId: track.id });
							});
						}

						// Remove audio element
						if (hiddenAudio.parentNode) {
							hiddenAudio.parentNode.removeChild(hiddenAudio);
						}

						const duration = Date.now() - startTime;
						logger.info('Enhanced audio-based media activation completed', {
							duration: `${duration}ms`,
							method: 'primary_audio_element',
							success: true,
							audioRemoved: true,
							streamStopped: true
						});

						resolve(true);
					} catch (cleanupError) {
						logger.error('Error during enhanced audio cleanup', {
							error: cleanupError.message,
							duration: `${Date.now() - startTime}ms`
						});
						resolve(false);
					}
				};

				// Event listeners for audio element
				hiddenAudio.addEventListener('canplaythrough', () => {
					logger.info('Enhanced hidden audio can play through', {
						currentTime: hiddenAudio.currentTime,
						duration: hiddenAudio.duration || 'unknown',
						readyState: hiddenAudio.readyState
					});
				});

				hiddenAudio.addEventListener('playing', () => {
					logger.info('Enhanced hidden audio started playing', {
						currentTime: hiddenAudio.currentTime,
						volume: hiddenAudio.volume,
						streamActive: destination.stream.active
					});

					// Clean up after 1.2 seconds for reliable activation
					setTimeout(cleanup, 1200);
				});

				hiddenAudio.addEventListener('error', (e) => {
					logger.error('Enhanced hidden audio error', {
						error: e.message || 'unknown_error',
						errorCode: e.code || 'unknown',
						duration: `${Date.now() - startTime}ms`
					});
					cleanup();
				});

				// Start the oscillator and play audio
				oscillator.start();
				oscillator.stop(audioCtx.currentTime + 1); // Play for 1.5 seconds

				hiddenAudio.play().catch(playError => {
					logger.error('Failed to play enhanced hidden audio', {
						error: playError.message,
						errorName: playError.name,
						duration: `${Date.now() - startTime}ms`
					});
					cleanup();
				});

				// Fallback cleanup - extended timeout for reliability
				setTimeout(() => {
					logger.warn('Enhanced audio activation timeout - forcing cleanup', {
						duration: `${Date.now() - startTime}ms`
					});
					cleanup();
				}, 1500);

			} catch (error) {
				logger.error('Enhanced audio-based activation failed', {
					error: error.message,
					duration: `${Date.now() - startTime}ms`
				});
				resolve(false);
			}
		});
	};

	// Manual audio activation fallback for browser refresh issues
	const handleManualAudioActivation = async () => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping manual audio activation - user is leaving call');
			return;
		}

		logger.info('Manual audio activation triggered by user interaction');

		try {
			// Ensure audio context is ready
			await checkAndResumeAudioContext();

			// Activate all volume systems (both media and in-call)
			const activationResults = await activateAllVolumeSystems();

			if (activationResults.mediaVolumeActivated || activationResults.inCallVolumeActivated) {
				// Setup consultant audio
				const setupSuccess = await setupConsultantAudioWithRetry(5, 500);

				if (setupSuccess) {
					logger.info('Manual audio activation successful', {
						mediaVolume: activationResults.mediaVolumeActivated,
						inCallVolume: activationResults.inCallVolumeActivated,
						communicationStream: activationResults.communicationStreamActivated
					});
				} else {
					logger.warn('Manual audio activation partially successful');
				}
			} else {
				logger.error('Manual audio activation failed - both volume systems failed');
			}
		} catch (error) {
			logger.error('Manual audio activation error', { error: error.message });
		}
	};

	// Enhanced audio-only media volume activation function
	const activateMediaVolume = async () => {
		// Check if user is leaving call
		if (isLeavingCallRef.current) {
			logger.info('Skipping media volume activation - user is leaving call');
			return false;
		}

		const startTime = Date.now();

		try {
			logger.info('Starting audio-only media volume activation', {
				timestamp: new Date().toISOString(),
				purpose: 'activate_media_volume_instead_of_incall_volume',
				method: 'audio_only_approach'
			});
			// toast.info('Starting media volume activation...');

			// Use enhanced audio activation approach
			const audioSuccess = await tryAudioActivation(startTime);

			if (audioSuccess) {
				logger.info('Audio-based media volume activation succeeded', {
					duration: `${Date.now() - startTime}ms`,
					method: 'audio_element',
					success: true
				});
				return true;
			} else {
				logger.warn('Audio-based media volume activation failed', {
					duration: `${Date.now() - startTime}ms`,
					method: 'audio_element',
					success: false
				});
				return false;
			}

		} catch (error) {
			const duration = Date.now() - startTime;
			logger.error('Audio-only media volume activation failed', {
				error: error.message,
				duration: `${duration}ms`,
				method: 'audio_element'
			});
			return false;
		}
	};

	// RTCPeerConnection-based In-Call Volume Activation
	const activateInCallVolume = async () => {
		try {
			logger.info('Starting RTCPeerConnection in-call volume activation');

			// Create communication-optimized stream
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
					channelCount: 1, // Mono like phone calls
					sampleRate: 48000, // Native browser sample rate - avoids resampling latency
					latency: 0.01 // Low latency for real-time communication
				}
			});

			// Use RTCPeerConnection to activate in-call volume (as requested)
			const pc = new RTCPeerConnection();
			stream.getTracks().forEach(track => pc.addTrack(track, stream));

			const offer = await pc.createOffer();
			await pc.setLocalDescription(offer);

			// Clean up after 1 second
			setTimeout(() => {
				stream.getTracks().forEach(track => track.stop());
				pc.close();
			}, 1000);

			return true;
		} catch (error) {
			logger.error('In-call volume activation failed', { error: error.message });
			return false;
		}
	};

	// Optimize Audio for Voice Communication
	const optimizeAudioForVoiceCommunication = async () => {
		try {
			const consultantAudio = document.getElementById("consultantAudio");
			if (!consultantAudio) {
				logger.warn('Consultant audio element not found for voice optimization');
				return false;
			}

			// Set communication-specific properties
			consultantAudio.setAttribute('x-webkit-airplay', 'deny');
			consultantAudio.setAttribute('disableRemotePlayback', '');

			// Try to set audio category hints for mobile browsers
			if (consultantAudio.mozAudioChannelType !== undefined) {
				consultantAudio.mozAudioChannelType = 'telephony'; // Firefox
			}

			logger.info('Audio element optimized for voice communication', {
				airplayDenied: true,
				remotePlaybackDisabled: true,
				firefoxTelephonyMode: consultantAudio.mozAudioChannelType === 'telephony'
			});

			return true;
		} catch (error) {
			logger.error('Audio voice communication optimization failed', { error: error.message });
			return false;
		}
	};

	// Master Dual Volume System Activation
	const activateAllVolumeSystems = async () => {
		logger.info('Starting dual volume system activation');

		const results = {
			mediaVolumeActivated: false,
			inCallVolumeActivated: false,
			communicationStreamActivated: false
		};

		try {
			// 1. Activate media volume (existing system)
			results.mediaVolumeActivated = await activateMediaVolume();

			// 2. Activate in-call volume using RTCPeerConnection
			results.inCallVolumeActivated = await activateInCallVolume();

			// 3. Optimize audio for voice communication
			results.communicationStreamActivated = await optimizeAudioForVoiceCommunication();

			// Log comprehensive results
			logger.info('Volume systems activation completed', {
				mediaVolume: results.mediaVolumeActivated,
				inCallVolume: results.inCallVolumeActivated,
				communicationStream: results.communicationStreamActivated,
				totalSuccess: results.mediaVolumeActivated || results.inCallVolumeActivated
			});

			// User feedback
			if (results.mediaVolumeActivated || results.inCallVolumeActivated) {
				logger.info('Dual volume system activation successful');
			} else {
				logger.warn('Both volume systems failed to activate');
			}

			return results;
		} catch (error) {
			logger.error('Dual volume system activation failed', { error: error.message });
			return results;
		}
	};

	// ==================== BACKEND CRASH PREVENTION IMPLEMENTATION ====================

	// Comprehensive Resource Cleanup
	const cleanupResources = async () => {
		try {
			logger.info('Starting comprehensive cleanup');

			// Clear all intervals first to prevent memory leaks
			if (consultantIntervalId) {
				clearInterval(consultantIntervalId);
				consultantIntervalId = null;
			}

			if (bitrateInterval) {
				clearInterval(bitrateInterval);
				setBitrateInterval(null);
			}

			if (bitrateTimeout) {
				clearTimeout(bitrateTimeout);
				bitrateTimeout = null;
			}

			// Reset state variables
			setIsPluginAttached(false);

			logger.info('Resources cleaned up successfully');
		} catch (error) {
			logger.error('Error during cleanup', { error: error.message });
		}
	};

	/** Re-publish tracks after a completed reconnect; duplicate-publish errors are benign. */
	const rePublishLocalTracksWA = async () => {
		try {
			if (!agoraClient) return;
			const tracksToPublish = [localAudioTrackAgora, localVideoTrackAgora].filter(Boolean);
			if (tracksToPublish.length > 0) {
				await agoraClient.publish(tracksToPublish);
				logger.info("Agora local tracks re-published", {
					event: "local_tracks_republished",
					hasAudio: !!localAudioTrackAgora,
					hasVideo: !!localVideoTrackAgora
				});
			}
		} catch (e) {
			if (!e.message?.includes("ALREADY")) {
				logger.error("Failed to re-publish tracks", { error: e.message });
			}
		}
	};

	/**
	 * Give Agora's internal recovery time to finish before forcing a leave/join cycle.
	 * The timer and mutex prevent overlapping reconnects on unstable mobile networks.
	 */
	const scheduleManualReconnectWA = () => {
		if (agoraReconnectTimer) clearTimeout(agoraReconnectTimer);
		agoraReconnectTimer = setTimeout(async () => {
			agoraReconnectTimer = null;
			const invalidStates = ["CONNECTING", "RECONNECTING", "CONNECTED"];
			if (!agoraClient || isLeavingCallRef.current || invalidStates.includes(agoraClient.connectionState)) return;
			// Prevent duplicate concurrent manual reconnect attempts
			if (isManuallyReconnecting) {
				logger.info("Manual reconnect already in progress - skipping duplicate attempt");
				return;
			}
			isManuallyReconnecting = true;
			logger.info("Attempting manual Agora reconnect for web assistant...");
			try {
				try { await agoraClient.leave(); } catch (e) { /* ignore */ }
				if (webAsstRoomDetails?.appId && webAsstRoomDetails?.channel && webAsstRoomDetails?.token) {
					await agoraClient.join(
						webAsstRoomDetails.appId,
						webAsstRoomDetails.channel,
						webAsstRoomDetails.token,
						AGORA_UID_WEBASSISTANT
					);
					await rePublishLocalTracksWA();
					toast.success("Reconnected successfully");
				} else {
					// No room details available - cannot reconnect; avoid resetting isPluginAttached
					// which would re-trigger initAgora with stale/no token (Bug 2)
					logger.warn("Manual reconnect skipped - no room details available");
				}
			} catch (e) {
				logger.error("Manual reconnect failed", { error: e.message });
				toast.error("Reconnection failed. Please check your network.");
				agoraReconnectTimer = setTimeout(() => scheduleManualReconnectWA(), 15000);
			} finally {
				isManuallyReconnecting = false;
			}
		}, 20000); // Give Agora SDK 20s to self-recover on mobile before manual intervention
	};

	/** Release every Agora-owned resource. Safe to call after partial initialization. */
	const cleanupAgora = async () => {
		try {
			logger.info("Agora cleanup started", {
				event: "cleanup_started",
				connectionState: agoraClient?.connectionState || "none",
				hasLocalAudio: !!localAudioTrackAgora,
				hasLocalVideo: !!localVideoTrackAgora
			});
			// Stop bitrate interval so it doesn't poll a null agoraClient after leave
			if (bitrateTimeout) { clearInterval(bitrateTimeout); bitrateTimeout = null; }
			if (agoraReconnectTimer) {
				clearTimeout(agoraReconnectTimer);
				agoraReconnectTimer = null;
			}
			networkQualityDownlinkLevel = 0;
			// Global SDK listeners are independent of the client. Remove them before any
			// awaited cleanup so a failed leave cannot leak handlers into the next call.
			if (agoraSdk && agoraAutoplayFailedHandler) {
				agoraSdk.off("autoplay-failed", agoraAutoplayFailedHandler);
				agoraAutoplayFailedHandler = null;
			}
			if (agoraSdk && agoraAudioContextStateHandler) {
				agoraSdk.off("audio-context-state-changed", agoraAudioContextStateHandler);
				agoraAudioContextStateHandler = null;
			}
			// Clear remote user references immediately to prevent stale audio manipulation
			remoteUsers = {};
			if (localAudioTrackAgora) {
				localAudioTrackAgora.stop();
				localAudioTrackAgora.close();
				localAudioTrackAgora = null;
			}
			if (localVideoTrackAgora) {
				localVideoTrackAgora.stop();
				localVideoTrackAgora.close();
				localVideoTrackAgora = null;
			}
			if (agoraClient) {
				agoraClient.removeAllListeners();
				await agoraClient.leave();
				agoraClient = null;
			}
			logger.info("Agora cleanup completed", { event: "cleanup_completed" });
		} catch (e) {
			logger.error("Agora cleanup failed", { event: "cleanup_failed", error: e.message });
		}
	};

	/**
	 * Start consultant playback through the SDK and verify that Agora's AudioContext is
	 * running. Agora's play() API is synchronous, so autoplay rejection is reported by
	 * the global SDK event; the post-play context check closes the race where that event
	 * is delayed or omitted by a mobile browser.
	 */
	const playConsultantAudio = async (audioTrack, reason) => {
		if (!audioTrack || isConsultantAudioBlocked || isLeavingCallRef.current) return false;

		try {
			if (agoraSdk) {
				await agoraSdk.resumeAudioContext();
			}
			audioTrack.play();

			// Yield once so Agora can report the AudioContext state established by play().
			await Promise.resolve();
			const contextState = agoraSdk?.getCurrentMonotonicTime ? "sdk_ready" : "unknown";
			logger.info("Consultant audio playback requested", {
				event: "consultant_audio_play_requested",
				reason,
				contextState
			});
			return true;
		} catch (error) {
			logger.warn("Consultant audio requires user interaction", {
				event: "consultant_audio_play_blocked",
				reason,
				error: error.message
			});
			hasExplicitAudioUnlockRef.current = false;
			setShowAudioResumePopup(true);
			return false;
		}
	};

	// ==================== END DUAL VOLUME SYSTEM IMPLEMENTATION ====================

	useEffect(() => {
		// Enhanced browser refresh detection with multiple checks
		const hasSession = !!sessionStorage.getItem("WastSessionId");
		const hasState = !!state;
		const hasRoomDetails = !!webAsstRoomDetails;

		// Multiple indicators of page refresh vs fresh start
		const isPageRefresh = !hasState && hasSession;
		const mountType = isPageRefresh ? 'page_refresh' : 'component_mount';

		// Set a timestamp to help with debugging timing issues
		const mountTimestamp = Date.now();
		sessionStorage.setItem('lastMountTime', mountTimestamp.toString());

		// Component mount/refresh logging
		logger.logComponentMount('WebAssistantCallScreen', {
			state: hasState,
			webAsstRoomDetails: hasRoomDetails,
						mountType,
			isPageRefresh,
			sessionExists: hasSession,
			mountTimestamp
		});

		// Initialize audio context with refresh-aware logic
		initializeAudioContext();

		// Enhanced audio activation for browser refresh scenarios
		// const activateAudioForRefresh = async () => {
		// 	if (isPageRefresh) {
		// 		// Immediately activate media volume on refresh detection
		// 		activateMediaVolume();

		// 		logger.info('Browser refresh detected - ensuring audio activation', {
		// 			mountType,
		// 			sessionExists: !!sessionStorage.getItem("WastSessionId")
		// 		});
		// 		toast.info('Browser refresh detected - restoring audio...');

		// 		// Wait a moment for audio context to be properly initialized on refresh
		// 		await new Promise(resolve => setTimeout(resolve, 200));

		// 		// Check if audio context is still suspended after initialization
		// 		if (audioContext && audioContext.state === 'suspended') {
		// 			toast.warning('Audio context still suspended - trying to resume...');
		// 			try {
		// 				await audioContext.resume();
		// 				toast.success('Audio context resumed before media activation!');
		// 			} catch (error) {
		// 				logger.warn('Failed to resume audio context before media activation', { error: error.message });
		// 				toast.warning('Audio context resume failed - proceeding with media activation...');
		// 			}
		// 		}

		// 		// Multiple attempts for browser refresh to ensure audio works
		// 		let activationSuccess = false;
		// 		for (let attempt = 1; attempt <= 3 && !activationSuccess; attempt++) {
		// 			logger.info(`Audio activation attempt ${attempt}/3 for browser refresh`);
		// 			toast.info(`Audio activation attempt ${attempt}/3...`);

		// 			// Ensure audio context is ready before each attempt
		// 			await checkAndResumeAudioContext();

		// 			activationSuccess = await activateMediaVolume();

		// 			if (!activationSuccess && attempt < 3) {
		// 				toast.warning(`Attempt ${attempt} failed - retrying...`);
		// 				// Wait a bit before retry
		// 				await new Promise(resolve => setTimeout(resolve, 500));
		// 			}
		// 		}

		// 		if (activationSuccess) {
		// 			logger.info('Audio activation successful for browser refresh');
		// 			toast.success('Audio restored after browser refresh!');
		// 		} else {
		// 			logger.warn('Audio activation failed after multiple attempts on browser refresh');
		// 			toast.warning('Audio may need manual activation - click anywhere to enable');

		// 			// Add a one-time click listener to manually resume audio context
		// 			const handleManualResume = async () => {
		// 				if (audioContext && audioContext.state === 'suspended') {
		// 					try {
		// 						await audioContext.resume();
		// 						toast.success('Audio manually resumed by click!');
		// 						await activateMediaVolume();
		// 						document.removeEventListener('click', handleManualResume);
		// 					} catch (error) {
		// 						toast.error('Manual resume failed');
		// 					}
		// 				}
		// 			};
		// 			document.addEventListener('click', handleManualResume, { once: true });
		// 		}

		// 		return activationSuccess;
		// 	} else {
		// 		// Normal first-time activation
		// 		toast.info('First-time audio activation...');
		// 		return await activateMediaVolume();
		// 	}
		// };


		// Function to activate media volume
		const activateAudio = async () => {
			// Check if user is leaving call
			if (isLeavingCallRef.current) {
				logger.info('Skipping audio activation - user is leaving call');
				return false;
			}

			try {
				const activationSuccess = await activateMediaVolume();
				if (activationSuccess) {
					logger.info('Audio successfully activated');
					//toast.success('Audio restored!');
				} else {
					logger.warn('Audio activation failed');
					//toast.warning('Audio activation failed');
				}
				return activationSuccess;
			} catch (error) {
				logger.error('Error during audio activation', { error: error.message });
				//toast.error('Failed to activate audio');
				return false;
			}
		};

		// Function to resume the audio context if it's suspended
		const resumeAudioContextIfNeeded = async () => {
			if (audioContext && audioContext.state === 'suspended') {
				//toast.warning('Audio context is suspended - trying to resume...');
				try {
					await audioContext.resume();
					//toast.success('Audio context resumed!');
				} catch (error) {
					logger.warn('Failed to resume audio context', { error: error.message });
					//toast.warning('Audio context resume failed');
				}
			}
		};

		// Enhanced audio activation for browser refresh scenarios
		const activateAudioForRefresh = async () => {
			// Check if user is leaving call
			if (isLeavingCallRef.current) {
				logger.info('Skipping audio activation - user is leaving call');
				return false;
			}

			// Check if it's a page refresh
			if (isPageRefresh) {
				logger.info('Browser refresh detected', {
					mountType,
					sessionExists: !!sessionStorage.getItem("WastSessionId")
				});

				// Wait for audio context initialization
				await new Promise(resolve => setTimeout(resolve, 200));

				// First attempt: Try to resume audio context
				await resumeAudioContextIfNeeded();

				// Try to activate audio and handle retries
				let activationSuccess = false;
				for (let attempt = 1; attempt <= 3 && !activationSuccess; attempt++) {
					// Check again inside the loop in case user leaves during retry
					if (isLeavingCallRef.current) {
						logger.info('Stopping audio activation attempts - user is leaving call');
						return false;
					}

					logger.info(`Audio activation attempt ${attempt}/3 for browser refresh`);
					//toast.info(`Audio activation attempt ${attempt}/3...`);

					// Ensure audio context is ready before each attempt
					await checkAndResumeAudioContext();

					activationSuccess = await activateAudio();

					if (!activationSuccess && attempt < 3) {
						// Check again before starting retry delay
						if (isLeavingCallRef.current) {
							logger.info('Stopping audio activation retries - user is leaving call');
							return false;
						}

						//toast.warning(`Attempt ${attempt} failed - retrying...`);
						// Wait before retrying
						const retryTimeoutId = setTimeout(() => { }, 500);
						audioRetryTimeoutsRef.current.push(retryTimeoutId);
						await new Promise(resolve => setTimeout(resolve, 500));
					}
				}

				// If audio activation failed after multiple attempts, allow manual activation
				if (!activationSuccess) {
					logger.warn('Audio activation failed after multiple attempts');
					//toast.warning(' Audio may need manual activation - click anywhere to enable');

					const handleManualResume = async () => {
						if (audioContext && audioContext.state === 'suspended') {
							await resumeAudioContextIfNeeded();
							await activateAudio();
							document.removeEventListener('click', handleManualResume);
						}
					};
					document.addEventListener('click', handleManualResume, { once: true });
				}
				return activationSuccess;
			} else {
				// Normal first-time activation
				return await activateAudio();
			}
		};



		// Activate media volume on both component mount AND refresh for consistent audio experience
		audioActivationTimeoutRef.current = setTimeout(() => {
			// Check if user is leaving call before starting activation
			if (isLeavingCallRef.current) {
				logger.info('Skipping audio activation timeout callback - user is leaving call');
				return;
			}

			activateAudioForRefresh().then((success) => {
				// Check again after activation completes
				if (isLeavingCallRef.current) {
					logger.info('Skipping post-activation setup - user is leaving call');
					return;
				}

				logger.info('Media volume activation completed', {
					mountType,
					isPageRefresh,
					success,
					purpose: 'ensure_media_volume_for_consultant_audio'
				});

				// Additional setup for consultant audio after initial activation
				consultantAudioSetupTimeoutRef.current = setTimeout(() => {
					// Check again before consultant audio setup
					if (isLeavingCallRef.current) {
						logger.info('Skipping consultant audio setup timeout - user is leaving call');
						return;
					}

					setupConsultantAudioWithRetry().then((setupSuccess) => {
						// Check if user is leaving call before processing result
						if (isLeavingCallRef.current) {
							logger.info('Consultant audio setup result ignored - user leaving call');
							return;
						}

						if (setupSuccess) {
							logger.info('Initial consultant audio setup completed', {
								mountType,
								isPageRefresh
							});
						}
					});
				}, isPageRefresh ? 1500 : 1000); // Longer delay for refresh scenarios

			}).catch((error) => {
				logger.error('Media volume activation failed', {
					mountType,
					isPageRefresh,
					error: error.message
				});
			});
		}, isPageRefresh ? 300 : 100); // Longer initial delay for refresh scenarios

		// Check initial network type and warn if on cellular
		const initialNetworkInfo = checkNetworkType();
		checkDataUsageWarning();

		// Initialize previous network category for change detection
		setPreviousNetworkCategory(initialNetworkInfo.category);

		// Enhanced initial state logging for mount/refresh
		const logInitialState = () => {
			const networkInfo = getNetworkInfo();
			const consultantAudio = document.getElementById("consultantAudio");

			const essentialState = {
				networkType: networkInfo.connectionType || 'unknown',
				effectiveType: networkInfo.effectiveType || 'unknown',
				networkCategory: networkInfo.isWiFi ? 'WiFi' : networkInfo.isCellular ? 'Cellular' : 'Unknown',
				sinkId: consultantAudio ? (consultantAudio.sinkId || 'default') : 'no-audio-element',
				volume: consultantAudio ? Math.round(consultantAudio.volume * 100) : 0,
				audioElement: consultantAudio ? 'present' : 'missing',
				mountType,
				isPageRefresh
			};

			// Show enhanced summary toast with mount type
			const networkSummary = `${essentialState.networkCategory} (${essentialState.effectiveType})`;
			const volumeSummary = `Vol: ${essentialState.volume}%`;
			const sinkSummary = `Sink: ${essentialState.sinkId}`;
			const mountIndicator = isPageRefresh ? 'Refresh' : 'Start';

			//toast.info(`${mountIndicator} | ${networkSummary} | ${volumeSummary} | ${sinkSummary}`);
		};

		// Log initial network information - essential only
		const initialNetInfo = getNetworkInfo();
		const initialAudio = document.getElementById("consultantAudio");
		logger.warn(`Initial Setup - ${mountType}`, {
			networkType: initialNetInfo.connectionType || 'unknown',
			effectiveType: initialNetInfo.effectiveType || 'unknown',
			networkCategory: initialNetInfo.isWiFi ? 'WiFi' : initialNetInfo.isCellular ? 'Cellular' : 'Unknown',
			sinkId: initialAudio ? (initialAudio.sinkId || 'default') : 'no-audio-element',
			volume: initialAudio ? Math.round(initialAudio.volume * 100) : 0,
			audioElement: initialAudio ? 'present' : 'missing',
			mountType,
			isPageRefresh
		});

		// Enhanced state logging with mount/refresh context
		setTimeout(() => {
			logInitialState();
		}, 1000); // Delay to ensure audio elements are created

		// Set up network monitoring with enhanced logging
		if ('connection' in navigator) {
			const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
			if (connection) {
				// Log that we're setting up network monitoring
				logger.warn('Network monitoring setup', {
					connectionType: connection.type || 'unknown',
					effectiveType: connection.effectiveType || 'unknown',
					mountType
				});
				connection.addEventListener('change', handleNetworkChange);
			}
		} else {
			// Device doesn't support navigator.connection API (iPhone/Samsung)
			logger.warn('Navigator.connection API not supported - using alternative detection', {
				userAgent: navigator.userAgent.substring(0, 100),
				mountType
			});
		}

		// Set up universal online/offline monitoring (works on all devices)
		window.addEventListener('online', handleOnlineStatus);
		window.addEventListener('offline', handleOnlineStatus);

		// Set up mobile-specific network detection
		const cleanupMobileNetworkDetection = setupMobileNetworkDetection();

		// Set up audio device change monitoring
		const handleMediaDeviceChange = async () => {
			logger.info('Media devices changed - re-enumerating', { mountType });
			await handleAudioSinkChange();
			await logAvailableMediaDevices();
			logCurrentAudioSinks();
		};

		if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
			logger.warn('Audio device monitoring setup', { mountType });
			navigator.mediaDevices.addEventListener('devicechange', handleMediaDeviceChange);
		}

		// Set up manual audio activation for browser refresh scenarios
		const handleDocumentClick = async () => {
			// Only trigger on browser refresh if audio seems to be having issues
			if (isPageRefresh) {
				const consultantAudio = document.getElementById("consultantAudio");
				if (!consultantAudio || consultantAudio.volume === 0 || consultantAudio.muted) {
					await handleManualAudioActivation();
					// Remove the listener after one successful activation
					document.removeEventListener('click', handleDocumentClick);
				}
			}
		};

		// Add click listener for browser refresh scenarios only
		if (isPageRefresh) {
			document.addEventListener('click', handleDocumentClick, { once: true });
		}

		// Monitor consultant audio element
		const handleConsultantAudioError = (event) => {
			console.error('Consultant audio error:', event);
			logger.error('Consultant audio error occurred', {
				error: event.message,
				sinkId: audioElement?.sinkId || 'default',
				elementId: audioElement?.id || 'consultantAudio'
			});
		};

		const audioElement = document.getElementById("consultantAudio");
		if (audioElement) {
			// Audio event listeners
			audioElement.addEventListener('play', handleAudioStateChange);
			audioElement.addEventListener('pause', handleAudioStateChange);
			audioElement.addEventListener('volumechange', handleAudioStateChange);
			audioElement.addEventListener('loadstart', handleAudioStateChange);
			audioElement.addEventListener('canplay', handleAudioStateChange);
			audioElement.addEventListener('error', handleConsultantAudioError);
		}

		// Cleanup function
		return () => {
			logger.logComponentUnmount('WebAssistantCallScreen', { timestamp: new Date().toISOString() });

			// Stop all audio activation processes
			stopAllAudioActivation();

			// Clear network change debounce timeout
			clearTimeout(networkChangeTimeout.current);

			if ('connection' in navigator) {
				const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
				if (connection) {
					connection.removeEventListener('change', handleNetworkChange);
				}
			}

			window.removeEventListener('online', handleOnlineStatus);
			window.removeEventListener('offline', handleOnlineStatus);
			cleanupMobileNetworkDetection();

			if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
				navigator.mediaDevices.removeEventListener('devicechange', handleMediaDeviceChange);
			}

			// Clean up manual audio activation listener
			document.removeEventListener('click', handleDocumentClick);

			const audioElement = document.getElementById("consultantAudio");
			if (audioElement) {
				audioElement.removeEventListener('play', handleAudioStateChange);
				audioElement.removeEventListener('pause', handleAudioStateChange);
				audioElement.removeEventListener('volumechange', handleAudioStateChange);
				audioElement.removeEventListener('loadstart', handleAudioStateChange);
				audioElement.removeEventListener('canplay', handleAudioStateChange);
				audioElement.removeEventListener('error', handleConsultantAudioError);
			}

			// Only close audio context on actual component unmount, not during refresh
			// Audio context should persist across page refreshes to maintain audio functionality
			if (audioContext && audioContext.state !== 'closed') {
				logger.info('Component cleanup - audio context preserved', {
					currentState: audioContext.state,
					reason: 'preserve_for_refresh'
				});
				// Don't close audio context to prevent "closed" state issues on refresh
				// audioContext.close(); // COMMENTED OUT to fix refresh audio issues
			}

			// Comprehensive cleanup on component unmount
			cleanupResources().catch((error) => {
				logger.error('Error during component unmount cleanup', { error: error.message });
			});
		};
	}, [audioContext]);

	useEffect(() => {
		socketRef.current?.on("DRAW_TEXT", handleDrawText);

		return () => {
			socketRef.current?.off("DRAW_TEXT", handleDrawText);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [socketRef.current, handleDrawText]);

	useEffect(() => {
		setWebAsstSelfMute(sessionStorage.getItem("wastSelfMuted") === "true");
	}, [webAsstSelfMute]);

	useEffect(() => {
		//Commented code required for reference
		//const getRoom = sessionStorage.getItem("Room_Id");
		// if (getRoom != null) {
		//   onJoinCall();
		// }

		if (showMediaAccessPopup && isMobileDevice) {
			setShowMediaAccessPopup(true);
		}

		// storing wast session id in sessionstorage
		if (state?.callSessionId?.length > 0 && state?.callSessionId != undefined) {
			sessionStorage.setItem("WastSessionId", JSON.stringify(state.callSessionId));
		}

		if (state != null) {
			let callSession = state.callSessionId;
			socketRef.current?.on(WAST_CALL_TRANSFERRED, async () => {
				// Mark as leaving before cleanup
				isLeavingCallRef.current = true;
				stopAllAudioActivation();

				if (mediaRecorder?.state === "recording") {
					mediaRecorder?.stop();
				}
				await cleanupAgora();
				handleReset();

				// Clear audio test completion to ensure popup shows on next join
				sessionStorage.removeItem('audioTestCompleted');

				navigate("/webAssistantJoin/" + callSession);
				//toast.warning(t("responseMsg.msg_sessionExpired"));
			});
		}

		// Detect a page refresh / reconnect independently of `state`. iOS Safari restores
		// react-router's location.state across a reload (Chrome/Android give null), so keying
		// the rejoin off `state == null` silently skips it on Safari and the call never
		// reconnects. The reliable signal is: we have a stored session but the in-memory
		// socket and room details are both gone (wiped by the reload).
		const isWastReconnect = !socketRef.current && !!sessionStorage.getItem("WastSessionId");

		if ((state == null || isWastReconnect) && !joinInitiatedRef.current) {
			joinInitiatedRef.current = true;
			// Skip emitting WEB_AST_CALL_INITIATION if room details already exist in context.
			// The initAgora useEffect will handle the join automatically, preventing a
			// second join attempt on page refresh when SocketContext state persisted (Bug 5).
			if (!webAsstRoomDetails?.roomId) {
				//RejoinWast();
				onJoinCall();
			}
		}

		// Re-register the WAST -> room socket mapping on every (re)connect (covers the
		// initial-call path where onJoinCall isn't invoked because we arrived from the Join page).
		// iOS Safari drops the websocket when backgrounded/idle and socket.io reconnects with a
		// NEW socket.id; without re-emitting WEB_AST_CALL_INITIATION the server keeps routing
		// CONSULTANT_END_CALL / ROOM_DESTROYED to the dead socket. Idempotent via _wastReRegisterBound.
		if (socketRef.current && !socketRef.current._wastReRegisterBound) {
			socketRef.current._wastReRegisterBound = true;
			socketRef.current.on("connect", () => {
				if (isLeavingCallRef.current) return;
				const storedSession = sessionStorage.getItem("WastSessionId");
				if (!storedSession) return;
				logger.warn('Re-registering WAST with room on socket (re)connect', {
					socketId: socketRef.current?.id,
				});
				// Lightweight re-register: refreshes only the server's socket-id mapping,
				// without re-running the full initiation flow (WAST_CALL_TRANSFERRED / DB / JOIN_ROOM).
				socketRef.current?.emit(WEB_AST_RECONNECT, {
					sessionId: JSON.parse(storedSession),
				});
			});
		}

		// socket event on if glass user end call
		socketRef.current?.on(ROOM_DESTROYED, async () => {
			// Mark as leaving before cleanup
			isLeavingCallRef.current = true;
			stopAllAudioActivation();

			if (mediaRecorder?.state === "recording") {
				mediaRecorder?.stop();
			}
			await cleanupAgora();
			handleReset();
			setEndCallDialog(true);
			// Stop background intervals so they don't keep hitting the server with the
			// ended sessionId (which triggers repeated "Call Session is expired." toasts).
			clearInterval(timeoutId);
			clearInterval(consultantIntervalId);
			if (bitrateTimeout) { clearInterval(bitrateTimeout); bitrateTimeout = null; }
			sessionStorage.removeItem("webAssistantName");
			sessionStorage.removeItem("consultantName");
			sessionStorage.removeItem("WastSessionId");
			sessionStorage.removeItem("volumeConfirmed");
			sessionStorage.removeItem("mediaAccessGranted");
			sessionStorage.removeItem("wastSelfMuted");
		});

		socketRef.current?.on(CALL_USER_LEFT, () => {
			// canvas.clear();
			if (mediaRecorder?.state === "recording") {
				mediaRecorder.stop();
			}
			setRecording(false);
			setUserLeftAlert(true);
			setIsRemoteVideo(false);
		});

		socketRef.current?.on(CONSULTANT_END_CALL, async () => {
			// Mark as leaving before cleanup
			isLeavingCallRef.current = true;
			stopAllAudioActivation();

			if (mediaRecorder?.state === "recording") {
				mediaRecorder?.stop();
			}
			// Guard: localVideoRef.current can be null on iOS if the local video element
			// was torn down while backgrounded. Without this guard the assignment throws
			// and setEndCallDialog(true) below never runs, so the "meeting ended" popup
			// never appears even though the event arrived.
			if (localVideoRef.current) {
				localVideoRef.current.srcObject = null;
			}
			setLocalVideoStream(null);
			await cleanupAgora();
			handleReset();
			setEndCallDialog(true);
			stopMediaAccess();
			setTextMessages([]);
			sessionStorage.removeItem("webAssistantName");
			sessionStorage.removeItem("consultantName");
			sessionStorage.removeItem("WastSessionId");
			sessionStorage.removeItem("volumeConfirmed");
			sessionStorage.removeItem("mediaAccessGranted");
			sessionStorage.removeItem("wastSelfMuted");

			// Clear audio test completion to ensure popup shows on next join
			sessionStorage.removeItem('audioTestCompleted');

			dataConsumption(dataSent, true);
			clearInterval(consultantIntervalId);
			// Stop the background data-usage / bitrate intervals too. Otherwise they keep
			// POSTing the now-ended sessionId every 2 min and the server responds
			// "Call Session is expired.", which was being toasted repeatedly to the user.
			clearInterval(timeoutId);
			if (bitrateTimeout) { clearInterval(bitrateTimeout); bitrateTimeout = null; }

		});

		// AGORA: Unblock consultant audio when WAST is on main view.
		// Uses audioControlSeq to win any race against a concurrent OFF event that may
		// arrive out of order from the India-hosted socket server on high-latency (US) networks.
		socketRef.current?.on(ON_AUDIO_TO_WEB_AST, async () => {
			if (isLeavingCallRef.current) return;
			const mySeq = ++audioControlSeq;
			isConsultantAudioBlocked = false;

			try {
				// Abort if a newer ON/OFF event arrived while we were awaiting
				if (mySeq !== audioControlSeq) return;

				const consultantUser = remoteUsers[AGORA_UID_CONSULTANT];
				if (consultantUser && agoraClient) {
					// Use agoraClient.remoteUsers as authoritative source - avoids stale hasAudio flag
					// that can be false after an OFF->ON cycle in cross-region (US) scenarios.
					const liveUser = agoraClient.remoteUsers?.find(u => u.uid === AGORA_UID_CONSULTANT) || consultantUser;
					try {
						await agoraClient.subscribe(liveUser, 'audio');
					} catch (subErr) {
						// Already subscribed is not an error - continue to play
						logger.warn('[WAST] ON_AUDIO subscribe warning (may already be subscribed)', { error: subErr.message });
					}
					if (mySeq !== audioControlSeq) return;
					if (liveUser.audioTrack) await playConsultantAudio(liveUser.audioTrack, "audio_route_enabled");
					setRemoteAudioTrack(liveUser.audioTrack || null);
				} else {
					// Consultant not in channel yet - user-published will handle subscription
					// since isConsultantAudioBlocked is now false
					logger.info('[WAST] ON_AUDIO - consultant not available yet, will subscribe in user-published');
				}
				logger.info('Consultant audio subscribed via ON_AUDIO_TO_WEB_AST');
			} catch (error) {
				logger.error('Error subscribing consultant audio', { error: error.message });
			}
		});

		// AGORA: Block and UNSUBSCRIBE consultant audio when WAST is NOT on main view.
		// audioControlSeq ensures that if ON arrives before this OFF resolves, the ON wins.
		socketRef.current?.on(OFF_AUDIO_TO_WEB_AST, async () => {
			if (isLeavingCallRef.current) return;
			++audioControlSeq; // invalidate any in-flight ON handler so it aborts its sequence check
			// Block flag FIRST so user-published won't re-subscribe
			isConsultantAudioBlocked = true;

			try {
				const consultantUser = remoteUsers[AGORA_UID_CONSULTANT];
				if (consultantUser && agoraClient) {
					if (consultantUser.audioTrack) consultantUser.audioTrack.stop();
					setShowAudioResumePopup(false);
					// Unsubscribe at transport level - guaranteed to stop all audio
					await agoraClient.unsubscribe(consultantUser, 'audio');
				}
				logger.info('Consultant audio unsubscribed via OFF_AUDIO_TO_WEB_AST');
			} catch (error) {
				// Safe to ignore - might already be unsubscribed
				logger.warn('[WAST] OFF_AUDIO unsubscribe warning:', error.message);
			}
		});

		socketRef.current?.on(RECORDING_STARTED, () => {
			setRecordingStarted(true);
		});

		socketRef.current?.on(RECORDING_ENDED, () => {
			setRecordingStarted(false);
		});

		const username = JSON.parse(sessionStorage.getItem("username"));
		let userRole = parseInt(JSON.parse(sessionStorage.getItem("role")));
		setUserDetails({ username: username, userrole: userRole });
		if (!socketRef.current && userRole === 3 && apis.socketURL) {
			socketRef.current = io(apis.socketURL);
		}
		return () => {
			setInCommingCall(false);
			setRoomDetails(null);
			setCallToAssistant({ open: false, UserID: null, AssistantName: "" });
			zoomLevel = 100;
			callToAssistantRef.current = {
				open: false,
				UserID: null,
				AssistantName: "",
			};
			socketRef.current?.off(ROOM_DESTROYED);
			socketRef.current?.off(CALL_USER_LEFT);
			socketRef.current?.off(WAST_CALL_TRANSFERRED);
			socketRef.current?.off(ON_AUDIO_TO_WEB_AST);
			socketRef.current?.off(OFF_AUDIO_TO_WEB_AST);
			if (mediaRecorder?.state === "recording") {
				mediaRecorder.stop();
			}
			clearTimeout(bitrateTimeout);
			bitrateTimeout = null;
			// canvas.clear();
			// canvasTxtRef.current = null;
		};
	}, [showMediaAccessPopup, socketRef.current]);

	useEffect(() => {
		// ====== AGORA: Initialize and join channel ======
		// Do NOT start Agora while the media-access popup is still open.
		// Agora's createMicrophoneAudioTrack() competes with the raw getUserMedia()
		// call in requestMediaAccess(), causing device conflicts that disconnect Agora
		// and trigger scheduleManualReconnectWA — the root cause of the 2-3× join on first connect.
		if (webAsstRoomDetails?.roomId && webAsstRoomDetails?.appId && !isPluginAttached && !showMediaAccessPopup) {
			const initAgora = async () => {
				// Mutex guard: prevent concurrent Agora joins (Bug 1)
				if (isJoiningRef.current) {
					return;
				}
				// Guard: skip re-initialization if Agora is already connected or connecting.
				// This prevents a double-join when the useEffect re-fires due to dependency
				// changes (e.g. webAsstRoomDetails token update, or showMediaAccessPopup
				// flipping to false on mobile) while a connection is already active.
				if (agoraClient && ["CONNECTED", "CONNECTING", "RECONNECTING"].includes(agoraClient.connectionState)) {
					return;
				}
				isJoiningRef.current = true;
				let initStage = "stale_client_cleanup";
				try {
					logger.info("Agora initialization started", {
						event: "initialization_started",
						uid: AGORA_UID_WEBASSISTANT,
						isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
						hasToken: !!webAsstRoomDetails.token
					});
					// Clean up any stale Agora state from previous sessions
					// This is critical when WAST leaves and rejoins the same page
					if (agoraClient) {
						try {
							if (localAudioTrackAgora) { localAudioTrackAgora.stop(); localAudioTrackAgora.close(); localAudioTrackAgora = null; }
							if (localVideoTrackAgora) { localVideoTrackAgora.stop(); localVideoTrackAgora.close(); localVideoTrackAgora = null; }
							agoraClient.removeAllListeners();
							await agoraClient.leave();
						} catch (e) {
							logger.warn("Stale Agora cleanup error (safe to ignore)", { error: e.message });
						}
						agoraClient = null;
					}
					remoteUsers = {};
					if (agoraReconnectTimer) { clearTimeout(agoraReconnectTimer); agoraReconnectTimer = null; }
					networkQualityDownlinkLevel = 0;

					initStage = "sdk_load";
					const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
					AgoraRTC.setLogLevel(3);
					agoraSdk = AgoraRTC;
					if (agoraAutoplayFailedHandler) {
						AgoraRTC.off("autoplay-failed", agoraAutoplayFailedHandler);
					}
					if (agoraAudioContextStateHandler) {
						AgoraRTC.off("audio-context-state-changed", agoraAudioContextStateHandler);
					}
					agoraAutoplayFailedHandler = () => {
						logger.warn('Agora autoplay failed: user interaction is required', {
							event: "autoplay_failed"
						});
						hasExplicitAudioUnlockRef.current = false;
						setShowAudioResumePopup(true);
					};
					agoraAudioContextStateHandler = (currentState, previousState) => {
						logger.info('Agora audio context state changed', { currentState, previousState });
						if (currentState === "interrupted" || currentState === "suspended") {
							hasExplicitAudioUnlockRef.current = false;
							setShowAudioResumePopup(true);
						} else if (currentState === "running" && hasExplicitAudioUnlockRef.current) {
							setShowAudioResumePopup(false);
						}
					};
					AgoraRTC.on("autoplay-failed", agoraAutoplayFailedHandler);
					AgoraRTC.on("audio-context-state-changed", agoraAudioContextStateHandler);

					// h264 is universally hardware-accelerated (iOS, Android, Windows, Mac).
					// vp8 is software-only on most mobile chips -> CPU thermal throttle -> stream drops.
					// Single codec avoids Agora edge transcoding (not guaranteed on NA/EU edges).
					const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
					//agoraClient = AgoraRTC.createClient({ mode: "rtc", codec });
					agoraClient = AgoraRTC.createClient({ mode: "live", codec: "h264", role: "host" });

					// Dual-stream removed to reduce CPU overhead and audio latency

					agoraClient.on("user-published", async (user, mediaType) => {
						try {
							let didSubscribe = false;
							logger.info("Agora remote track published", {
								event: "remote_track_published",
								uid: user.uid,
								mediaType
							});
							// WAST should NEVER subscribe to Rokid audio (UID=3) under any condition
							if (user.uid === AGORA_UID_ROKID && mediaType === "audio") {
								remoteUsers[user.uid] = user;
								return;
							}

							// For consultant audio: only subscribe if NOT blocked.
							// When blocked (WAST in side view), we do NOT subscribe at all â€”
							// unsubscribe is the only guaranteed way to stop Agora audio.
							if (user.uid === AGORA_UID_CONSULTANT && mediaType === "audio") {
								if (!isConsultantAudioBlocked) {
									await agoraClient.subscribe(user, mediaType);
									didSubscribe = true;
								} else {
									logger.info('Agora consultant audio subscription skipped', {
										event: "remote_audio_blocked",
										uid: user.uid
									});
								}
							} else {
								await agoraClient.subscribe(user, mediaType);
								didSubscribe = true;
							}
							remoteUsers[user.uid] = user;

							// Only display consultant's video/audio (matching old behavior where
							// web assistant only subscribed to remoteFeed.rfdisplay === "consultant")
							if (user.uid === AGORA_UID_CONSULTANT) {
								if (mediaType === "video") {
									user.videoTrack.play(remoteVideoRef.current);
									setIsRemoteVideo(true);
									setIsConsultantCameraOff(false);
									// Only start timer and play join tone on first connection, not on Agora self-reconnect
									if (!hasPlayedJoinTone) {
										hasPlayedJoinTone = true;
										handleStart();
										if (callJoinToneRef?.current) {
											callJoinToneRef.current.currentTime = 0;
											callJoinToneRef.current.play();
										}
									}
								}

								if (mediaType === "audio") {
									if (user.audioTrack && !isConsultantAudioBlocked) {
										await playConsultantAudio(user.audioTrack, "remote_track_published");
									} else {
										logger.info('[WAST] Consultant audio NOT played (blocked or no track)');
									}
									setRemoteAudioTrack(user.audioTrack || null);
								}
							}
							if (didSubscribe) {
								logger.info("Agora remote track subscribed", {
									event: "remote_track_subscribed",
									uid: user.uid,
									mediaType
								});
							}
						} catch (subscribeError) {
							if (subscribeError?.code === 2021 || subscribeError?.message?.includes("no such stream id")) {
								logger.info('Remote track disappeared before subscription completed', { uid: user.uid, mediaType });
								return;
							}
							logger.error('Failed to subscribe to remote Agora track', {
								uid: user.uid,
								mediaType,
								error: subscribeError.message
							});
						}
					});

					agoraClient.on("user-unpublished", (user, mediaType) => {
						logger.info("Agora remote track unpublished", {
							event: "remote_track_unpublished",
							uid: user.uid,
							mediaType
						});
						if (mediaType === "audio" && user.uid === AGORA_UID_CONSULTANT) {
							user.audioTrack?.stop();
							setRemoteAudioTrack(null);
							setShowAudioResumePopup(false);
						}
						if (mediaType === "video" && user.uid === AGORA_UID_CONSULTANT) {
							user.videoTrack?.stop();
							setIsRemoteVideo(false);
							// Consultant is still in the channel (only video unpublished), so this is a
							// camera-off, not a disconnect. Show the avatar instead of "Connecting...".
							setIsConsultantCameraOff(true);
						}
					});

					// Fires when the consultant is present in the channel, regardless of what they publish.
					// If they joined (or were already present) with their camera OFF, no video
					// "user-published" ever arrives — so without this the WAST would sit on "Connecting..."
					// with a blank screen. Treat "present but no video" as camera-off and show the avatar.
					agoraClient.on("user-joined", (user) => {
						logger.info("Agora remote user joined", { event: "remote_user_joined", uid: user.uid });
						if (user.uid === AGORA_UID_CONSULTANT) {
							remoteUsers[user.uid] = user;
							if (!user.hasVideo) {
								setIsRemoteVideo(false);
								setIsConsultantCameraOff(true);
							}
							// Start the call timer / join tone on first presence even if the camera is off,
							// otherwise a camera-off consultant would never start the duration counter.
							if (!hasPlayedJoinTone) {
								hasPlayedJoinTone = true;
								handleStart();
								if (callJoinToneRef?.current) {
									callJoinToneRef.current.currentTime = 0;
									callJoinToneRef.current.play();
								}
							}
						}
					});

					agoraClient.on("user-left", (user) => {
						logger.info("Agora remote user left", { event: "remote_user_left", uid: user.uid });
						delete remoteUsers[user.uid];
						if (user.uid === AGORA_UID_CONSULTANT) {
							setRemoteAudioTrack(null);
							setIsRemoteVideo(false);
							// Consultant actually left the channel — back to the connecting state.
							setIsConsultantCameraOff(false);
							setShowAudioResumePopup(false);
						}
					});

					// Handle connection state changes for reconnection
					agoraClient.on("connection-state-change", (curState, prevState, reason) => {
						logger.info("Agora connection state changed", {
							event: "connection_state_changed",
							currentState: curState,
							previousState: prevState,
							reason: reason || "none"
						});
						if (curState === "CONNECTING") {
							// Agora started its own internal reconnect — cancel our pending manual
							// reconnect timer so both don't run concurrently. reason is always
							// undefined here because Agora only supplies reason on disconnection,
							// not on reconnect-start transitions.
							if (agoraReconnectTimer) { clearTimeout(agoraReconnectTimer); agoraReconnectTimer = null; }
							isManuallyReconnecting = false;
						} else if (curState === "RECONNECTING") {
							toast.warning("Network unstable, reconnecting...");
							// Agora is self-recovering — cancel any pending manual reconnect and reset flag
							if (agoraReconnectTimer) { clearTimeout(agoraReconnectTimer); agoraReconnectTimer = null; }
							isManuallyReconnecting = false;
						} else if (curState === "RECONNECTED" || curState === "CONNECTED") {
							if (curState === "RECONNECTED") toast.success("Connection restored");
							// Cancel any pending manual reconnect — connection is up
							if (agoraReconnectTimer) { clearTimeout(agoraReconnectTimer); agoraReconnectTimer = null; }
							isManuallyReconnecting = false;
							// Re-publish local tracks after reconnection
							rePublishLocalTracksWA();
						} else if (curState === "DISCONNECTED") {
							if (reason !== "LEAVE") {
								toast.error("Connection lost. Attempting to reconnect...");
								scheduleManualReconnectWA();
							}
						}
					});

					agoraClient.on("exception", (event) => {
						logger.warn("Agora SDK exception", {
							event: "sdk_exception",
							code: event.code,
							message: event.msg,
							uid: event.uid
						});
					});

					// Monitor network quality for adaptive behavior
					agoraClient.on("network-quality", (stats) => {
						networkQualityDownlinkLevel = stats.downlinkNetworkQuality;
						// The quality of the consultant video we RECEIVE depends only on our
						// downlink. Our uplink (sending our own camera) is irrelevant here —
						// keying off it wrongly forced the consultant stream to the low (blurry)
						// stream whenever our upload was congested (common on mobile).
						// Hysteresis: downgrade at >=4, upgrade at <=2, hold at 3 to avoid flapping.
						if (stats.downlinkNetworkQuality >= 5) {
							console.warn("WA: Poor downlink quality - downlink:", stats.downlinkNetworkQuality);
							Object.keys(remoteUsers).forEach(uid => {
								try { agoraClient.setRemoteVideoStreamType(Number(uid), 1); } catch (e) { /* ignore */ }
							});
						} else if (stats.downlinkNetworkQuality > 0 && stats.downlinkNetworkQuality <= 3) {
							Object.keys(remoteUsers).forEach(uid => {
								try { agoraClient.setRemoteVideoStreamType(Number(uid), 0); } catch (e) { /* ignore */ }
							});
						}
					});

					// Handle stream fallback events
					agoraClient.on("stream-fallback", (uid, isFallbackOrRecover) => {
						if (isFallbackOrRecover === "fallback") {
							toast.warning("Video quality reduced due to network conditions");
						}
					});

					// Handle token expiry
					agoraClient.on("token-privilege-will-expire", async () => {
						logger.info("Agora token will expire", { event: "token_will_expire" });
						// Request a new token proactively so renewToken() can apply it before
						// the current token actually expires, keeping the channel uninterrupted.
						const SessionId = sessionStorage.getItem("WastSessionId");
						if (socketRef.current && SessionId) {
							const callSession = JSON.parse(SessionId);
							socketRef.current.emit(WEB_AST_CALL_INITIATION, {
								webAssistantName: sessionStorage.getItem("webAssistantName"),
								sessionId: callSession,
							});
						}
					});

					agoraClient.on("token-privilege-did-expire", async () => {
						logger.warn("Agora token expired", { event: "token_expired" });
						toast.error("Session token expired. Reconnecting...");
						// Request a fresh token via socket instead of resetting isPluginAttached.
						// The server responds with JOIN_ROOM which updates webAsstRoomDetails.token,
						// then reconnectAgora fires automatically (Bug 2).
						const SessionId = sessionStorage.getItem("WastSessionId");
						if (socketRef.current && SessionId) {
							const callSession = JSON.parse(SessionId);
							socketRef.current.emit(WEB_AST_CALL_INITIATION, {
								webAssistantName: sessionStorage.getItem("webAssistantName"),
								sessionId: callSession,
							});
						} else {
							setIsPluginAttached(false);
						}
					});

					// Cloud Proxy mode 5 (auto): tries direct UDP first, silently falls back to
					// Agora TLS-443 proxy if UDP is blocked (corporate firewalls, hospital networks).
					// Safe always — overhead only when fallback actually triggers.
					try {
						await agoraClient.startProxyServer(5);
					} catch (proxyErr) {
						logger.warn('Cloud Proxy start failed, joining without proxy', { error: proxyErr.message });
					}

					// Web assistant uses deterministic UID=2
					initStage = "channel_join";
					const uid = await agoraClient.join(
						webAsstRoomDetails.appId,
						webAsstRoomDetails.channel,
						webAsstRoomDetails.token,
						AGORA_UID_WEBASSISTANT
					);
					logger.info("Agora channel joined", {
						event: "channel_joined",
						uid,
						connectionState: agoraClient.connectionState
					});

					// Release the raw getUserMedia stream so the mic/camera are free for Agora to claim
					if (mediaStreamRef.current) {
						mediaStreamRef.current.getTracks().forEach(track => track.stop());
						mediaStreamRef.current = null;
						setMediaStream(null);
					}

					// Create local audio+video tracks separately for mobile browser compatibility
					// Audio track must always be created (critical for conference call)
					initStage = "microphone_track_creation";
					localAudioTrackAgora = await AgoraRTC.createMicrophoneAudioTrack({
						AEC: true,  // Acoustic Echo Cancellation
						ANS: true,  // Automatic Noise Suppression
						AGC: true,  // Automatic Gain Control
						encoderConfig: "high_quality_stereo"
					});

					try {
						initStage = "camera_track_creation";
						// Use adaptive video config with proper bitrate for mobile
						const videoConfig = isMobile
							? {
								encoderConfig: { width: 640, height: 480, frameRate: 24, bitrateMin: 400, bitrateMax: 1000 },
								facingMode: isFrontCamera ? "user" : "environment",
								optimizationMode: "detail"
							}
							: {
								encoderConfig: { width: 1280, height: 720, frameRate: 30, bitrateMin: 800, bitrateMax: 2000 },
								facingMode: isFrontCamera ? "user" : "environment",
								optimizationMode: "detail"
							};
						localVideoTrackAgora = await AgoraRTC.createCameraVideoTrack(videoConfig);
					} catch (videoError) {
						logger.warn("Agora camera unavailable; continuing audio-only", {
							event: "camera_track_failed",
							error: videoError.message
						});
						localVideoTrackAgora = null;
					}

					const tracksToPublish = [localAudioTrackAgora, localVideoTrackAgora].filter(Boolean);
					initStage = "local_track_publish";
					await agoraClient.publish(tracksToPublish);
					logger.info("Agora local tracks published", {
						event: "local_tracks_published",
						hasAudio: !!localAudioTrackAgora,
						hasVideo: !!localVideoTrackAgora
					});

					if (localVideoTrackAgora && localVideoRef.current) {
						localVideoTrackAgora.play(localVideoRef.current);
					}
					setLocalVideoStream(localVideoTrackAgora?.getMediaStreamTrack() ? new MediaStream([localVideoTrackAgora.getMediaStreamTrack()]) : null);
					setLocalAudioTrack(localAudioTrackAgora);
					setIsPluginAttached(true);
					lastJoinedTokenRef.current = webAsstRoomDetails.token;
					calculateBitrate();
				} catch (error) {
					logger.error("Agora initialization failed", {
						event: "initialization_failed",
						stage: initStage,
						error: error.message,
						code: error.code
					});
					if (localAudioTrackAgora) {
						localAudioTrackAgora.stop();
						localAudioTrackAgora.close();
						localAudioTrackAgora = null;
					}
					if (localVideoTrackAgora) {
						localVideoTrackAgora.stop();
						localVideoTrackAgora.close();
						localVideoTrackAgora = null;
					}
					if (agoraClient) {
						agoraClient.removeAllListeners();
						try { await agoraClient.leave(); } catch (leaveError) {
							logger.warn("Agora rollback leave failed", { error: leaveError.message });
						}
						agoraClient = null;
					}
					setIsPluginAttached(false);
					setMediaAccessError("The call could not connect. Check your network, then select Allow to try again.");
					setShowMediaAccessPopup(true);
				} finally {
					isJoiningRef.current = false;
				}
			};
			initAgora();
		}

	}, [webAsstRoomDetails, isPluginAttached, showMediaAccessPopup]);

	// AGORA: Renew token when a fresh token arrives — no leave/join needed.
	// Token refresh is a lightweight SDK call that keeps the existing channel connection intact.
	// Full reconnection (leave + join) is handled separately by scheduleManualReconnectWA
	// when connection-state-change fires with curState === "DISCONNECTED".
	useEffect(() => {
		if (!webAsstRoomDetails?.token || !agoraClient || !isPluginAttached) return;
		if (isLeavingCallRef.current || isJoiningRef.current) return;

		agoraClient.renewToken(webAsstRoomDetails.token)
			.then(() => {
				lastJoinedTokenRef.current = webAsstRoomDetails.token;
				logger.info("Agora token renewed successfully");
			})
			.catch((error) => {
				logger.error("Agora token renewal failed", { error: error.message });
			});
	}, [webAsstRoomDetails?.token]);

	// useEffect for display network status
	useEffect(() => {
		if (failedPong === 2) toast.warning(t("responseMsg.msg_unstableConnection"));
		if (failedPong === 5) {
			toast.error(t("responseMsg.msg_connectionLostWithServer"));
			navigate("/dashboard/assistants");
		}
	}, [failedPong, navigate]);

	useEffect(() => {
		timeoutId = setInterval(() => {
			dataConsumption(dataSent, false);
			setDataUsageTimer(dataUsageTimer + 1);
		}, 120000);

		// Enhanced interval for comprehensive audio and network monitoring
		const monitoringInterval = setInterval(() => {
			// Universal connectivity check (works on all devices)
			const currentOnlineState = navigator.onLine;
			if (currentOnlineState !== isOnline) {
				logger.info('Connectivity state changed during monitoring', {
					previous: isOnline,
					current: currentOnlineState,
					source: 'periodic_check'
				});
				handleConnectivityChange(isOnline, 'periodic_monitoring');
			}

			// Check network status (only if supported)
			if (!navigator.onLine && isOnline) {
				handleOnlineStatus();
			}

			// Get current network info for comparison
			const currentNetworkInfo = getNetworkInfo();

			// Enhanced audio state monitoring for all devices
			const audioElement = document.getElementById("consultantAudio");
			if (audioElement) {
				const userSelfMuted = sessionStorage.getItem("wastSelfMuted") === "true";

				// Check for audio issues that might need recovery
				const hasAudioIssues = audioElement.paused ||
					audioElement.volume === 0 ||
					(audioElement.muted && !userSelfMuted);

				if (hasAudioIssues && navigator.onLine) {
					logger.warn('Audio issues detected during monitoring', {
						paused: audioElement.paused,
						volume: audioElement.volume,
						muted: audioElement.muted,
						userSelfMuted,
						sinkId: audioElement.sinkId || 'default',
						networkType: currentNetworkInfo.connectionType || 'unknown'
					});

					// Trigger audio recovery for detected issues
					setIsRecovering(true);
					performFullAudioRecovery('periodic_audio_check').then(() => {
						// Check if user is leaving call before updating state
						if (!isLeavingCallRef.current) {
							setIsRecovering(false);
						}
					}).catch((error) => {
						logger.error('Periodic audio recovery failed', {
							error: error.message
						});
						// Check if user is leaving call before updating state
						if (!isLeavingCallRef.current) {
							setIsRecovering(false);
						}
					});
				}

				// Check if audio context needs attention (universal support)
				if (audioContext && audioContext.state === 'suspended') {
					logger.warn('Audio context suspended during monitoring - attempting resume');
					checkAndResumeAudioContext().catch((error) => {
						logger.error('Failed to resume audio context during monitoring', {
							error: error.message
						});
					});
				}

				// Only log severe network issues
				if (currentNetworkInfo.isCellular && audioElement.networkState === 2) {
					logger.warn('Audio loading on cellular - potential issues', {
						networkType: currentNetworkInfo.effectiveType,
						volume: Math.round(audioElement.volume * 100)
					});
				}
			}

			// Mobile-specific: Check WebRTC connection health
			const isMobile = /iPhone|iPad|iPod|Android|BlackBerry|Windows Phone/i.test(navigator.userAgent);
			if (isMobile && currentOnlineState) {
				// Additional mobile connectivity verification
				// This helps catch network changes that mobile devices might not report
				fetch((process.env.REACT_APP_BASE_URL || window.location.origin) + '/health', {
					method: 'HEAD',
					cache: 'no-store',
					signal: AbortSignal.timeout(5000) // 5 second timeout
				}).catch((error) => {
					if (navigator.onLine) {
						logger.warn('Mobile connectivity issue detected despite online status', {
							error: error.message,
							userAgent: navigator.userAgent.substring(0, 50)
						});

						// Trigger recovery if we can't reach our own server
						setTimeout(() => {
							if (!isRecovering) {
								handleActualNetworkChange();
							}
						}, 2000);
					}
				});
			}

			// Only log data usage warnings on cellular (reduced frequency)
			if (currentNetworkInfo.isCellular && dataUsageTimer > 0 && dataUsageTimer % 10 === 0) {
				const dataUsageMinutes = dataUsageTimer * 2; // 2 minutes per interval
				logger.warn('Cellular data usage warning', {
					minutes: dataUsageMinutes,
					networkType: currentNetworkInfo.effectiveType
				});

				// if (dataUsageMinutes >= 20) { // Warn every 20 minutes
				// 	//toast.warning(`${dataUsageMinutes} minutes of cellular video calling`);
				// }
			}
		}, 30000); // Check every 30 seconds (reduced from 60 for better mobile support)

		return () => {
			clearInterval(monitoringInterval);
		};
	}, [dataUsageTimer, isOnline, audioContext]);

	useEffect(() => {
		const handleMuteYourself = () => {
			// AGORA: Disable local audio track
			if (localAudioTrackAgora) localAudioTrackAgora.setEnabled(false);
			setRecordingMute(true);
			// Note: Do NOT set recordingStarted here â€” recording is only controlled
			// by RECORDING_STARTED/RECORDING_ENDED events from consultant
		};
		const handleUnmuteYourself = () => {
			const selfWastMuted = sessionStorage.getItem("wastSelfMuted") === "true";
			if (!selfWastMuted) {
				// AGORA: Enable local audio track
				if (localAudioTrackAgora) localAudioTrackAgora.setEnabled(true);
			}
			setRecordingMute(false);
			// Note: Do NOT set recordingStarted here â€” recording is only controlled
			// by RECORDING_STARTED/RECORDING_ENDED events from consultant
		};

		socketRef.current?.on(MUTE_YOURSELF, handleMuteYourself);
		socketRef.current?.on(UNMUTE_YOURSELF, handleUnmuteYourself);

		return () => {
			socketRef.current?.off(MUTE_YOURSELF, handleMuteYourself);
			socketRef.current?.off(UNMUTE_YOURSELF, handleUnmuteYourself);
		};
	}, [recordingMute]);

	// AGORA: calculateBitrate using Agora stats
	const calculateBitrate = () => {
		if (bitrateTimeout) clearInterval(bitrateTimeout);
		bitrateTimeout = setInterval(async () => {
			if (!agoraClient) return;
			try {
				const localStats = agoraClient.getLocalVideoStats();
				if (localStats && localStats.sendBytes) {
					const bytesSentNow = localStats.sendBytes;
					if (bytesSentNow >= prevBytesSent) {
						const delta = bytesSentNow - prevBytesSent;
						dataSent = ((prevBytesSent + delta) / (1024 * 1024));
						prevBytesSent = bytesSentNow;
					}
				}
			} catch (e) {
				console.error("Failed to get Agora stats:", e);
			}
		}, 1000);
	};


	useEffect(() => {
		window.onbeforeunload = () => {
			if (mediaRecorder?.state === "recording") {
				mediaRecorder.stop();
				socketRef.current?.emit(RECORDING_ENDED, sessionIdRef.current);
				setRecordingStarted(false);
			}
			if (window.location.pathname.includes("/webassistantcall")) {
				dataConsumption(dataSent, false);
			}
			socketRef.current?.on(USER_MUTE, sessionIdRef.current);
		};
	}, []);

	// Function to handle ending the call
	const handleEndCall = async () => {
		// Mark as leaving immediately to prevent stale event handlers from firing
		isLeavingCallRef.current = true;

		// Stop all audio activation processes immediately
		stopAllAudioActivation();

		logger.logUserAction('end_call_initiated', {
			recordingState: mediaRecorder?.state
		});

		if (mediaRecorder?.state === "recording") {
			mediaRecorder.stop();
		}
		// Release DOM video/audio element srcObjects so camera indicator turns off
		if (localVideoRef.current) localVideoRef.current.srcObject = null;
		if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
		if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
		// Release raw getUserMedia stream if still held
		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach(t => t.stop());
			mediaStreamRef.current = null;
		}
		await cleanupAgora();
		stopMediaAccess();
		setLocalVideoStream(null);
		socketRef.current?.emit(END_CALL, sessionIdRef.current);

		// Clear audio test completion to ensure popup shows on next join
		sessionStorage.removeItem('audioTestCompleted');
		clearInterval(timeoutId);
		clearInterval(consultantIntervalId);
		sessionStorage.removeItem("webAssistantName");
		sessionStorage.removeItem("consultantName");
		sessionStorage.removeItem("WastSessionId");
		sessionStorage.removeItem("volumeConfirmed");
		sessionStorage.removeItem("mediaAccessGranted");
		sessionStorage.removeItem("wastSelfMuted");
		isJoiningRef.current = false;
		lastJoinedTokenRef.current = null;

		navigate("/webAssistantJoin/:sessionId");
		handleReset();
		setIsConferenceCall(false);
		isConsultantAudioBlocked = false;
		hasPlayedJoinTone = false;
		sessionStorage.removeItem("isConferenceCall");
		setRecordingMute(false);
		setTextMessages([]);
	};

	// Function to handle staying in the call
	const handleStayInCall = () => {
		logger.logUserAction('stay_in_call_selected', { timestamp: new Date().toISOString() });
		setUserLeftAlert(false);
		setNavigationBackPopup(false);
	};

	const swapVideos = () => {
		logger.logUserAction('swap_videos', {
			currentContainer1: container1,
			currentContainer2: container2,
			isSelfSecondary
		});

		const videoElement1 = remoteVideoRef.current;
		const videoElement2 = localVideoRef.current;
		if (videoElement1 || videoElement2) {
			const newContainer1 = container2;
			const newContainer2 = container1;
			const currentContainer1 = document.getElementById(container1);
			const currentContainer2 = document.getElementById(container2);
			if (currentContainer1 && currentContainer2) {
				isSelfSecondary = !isSelfSecondary;
				currentContainer1.appendChild(videoElement2);
				currentContainer2.appendChild(videoElement1);
			}
			setContainer1(newContainer1);
			setContainer2(newContainer2);
		}
		if (videoElement1) {
			setShowMuteIcon(!showMuteIcon);
		}
	};

	// Function to handle leaving the call
	const handleLeaveCall = async () => {
		// Mark as leaving immediately to prevent stale event handlers from firing
		isLeavingCallRef.current = true;
		joinInitiatedRef.current = false;

		// Stop all audio activation processes immediately
		stopAllAudioActivation();

		// Reset join mutex and token tracking so the next join works cleanly
		isJoiningRef.current = false;
		lastJoinedTokenRef.current = null;

		// Cancel pending network-change debounce timer to prevent post-leave reconnect
		if (networkChangeTimeout.current) {
			clearTimeout(networkChangeTimeout.current);
			networkChangeTimeout.current = null;
		}

		logger.logUserAction('leave_call_initiated', {
			dataUsageTimer,
			dataSent,
			consDataReceived
		});

		// Unregister audio socket listeners BEFORE async cleanup to prevent stale events
		socketRef.current?.off(ON_AUDIO_TO_WEB_AST);
		socketRef.current?.off(OFF_AUDIO_TO_WEB_AST);

		// Emit LEAVE_CALL BEFORE Agora cleanup so server clears reference immediately
		socketRef.current?.emit(LEAVE_CALL, sessionIdRef.current);

		clearInterval(timeoutId);
		const SessionId = sessionStorage.getItem("WastSessionId");
		const callSessionId = JSON.parse(SessionId);
		if (mediaRecorder?.state === "recording") {
			mediaRecorder.stop();
		}
		// Release DOM video/audio element srcObjects so the browser camera indicator turns off
		if (localVideoRef.current) localVideoRef.current.srcObject = null;
		if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
		if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
		// Release raw getUserMedia stream (camera flip fallback or pre-Agora init tracks)
		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach(t => t.stop());
			mediaStreamRef.current = null;
		}
		await cleanupAgora();
		stopMediaAccess();
		setLocalVideoStream(null);
		navigate("/webAssistantJoin/" + callSessionId);
		sessionStorage.removeItem("webAssistantName");
		sessionStorage.removeItem("consultantName");
		sessionStorage.removeItem("WastSessionId");
		sessionStorage.removeItem("volumeConfirmed"); // Clear volume confirmation flag
		sessionStorage.removeItem("mediaAccessGranted"); // Clear media access flag

		// Clear audio test completion to ensure popup shows on next join
		sessionStorage.removeItem('audioTestCompleted');

		handleReset();
		setIsConferenceCall(false);
		isConsultantAudioBlocked = false;
		hasPlayedJoinTone = false;
		sessionStorage.removeItem("isConferenceCall");
		isSelfSecondary = true;
		sessionStorage.removeItem("wastSelfMuted");
		dataConsumption(dataSent, true);
		clearInterval(consultantIntervalId);
		setTextMessages([]);
	};

	// outer container height
	const outerContainerRef = useRef(null);
	// const viewportHeightRef = useRef(window.innerHeight);
	const viewportHeightRef = useRef(document.documentElement.clientHeight);

	useEffect(() => {
		const initResizeOuterContainer = () => {
			setTimeout(() => {
				viewportHeightRef.current = document.documentElement.clientHeight;
				outerContainerRef.current.style.height = `${viewportHeightRef.current}px`;
			}, 10);
		};
		const handleResizeOuterContainer = () => {
			viewportHeightRef.current = document.documentElement.clientHeight;
			outerContainerRef.current.style.height = `${viewportHeightRef.current}px`;
		};

		initResizeOuterContainer();
		window.addEventListener("resize", handleResizeOuterContainer);
		return () => {
			window.removeEventListener("resize", handleResizeOuterContainer);
		};
	}, []);

	// Resume AudioContext on user interaction (for mobile/browser autoplay policies)
	useEffect(() => {
		function tryResumeAudioContext() {
			if (audioContext && audioContext.state === 'suspended') {
				audioContext.resume().then(() => {
					// Audio context resumed successfully - no logging needed
				}).catch((err) => {
					console.error('Failed to resume AudioContext:', err);
				});
			}
		}
		// Listen for user interaction events
		window.addEventListener('click', tryResumeAudioContext, true);
		window.addEventListener('touchstart', tryResumeAudioContext, true);
		return () => {
			window.removeEventListener('click', tryResumeAudioContext, true);
			window.removeEventListener('touchstart', tryResumeAudioContext, true);
		};
	}, [audioContext]);

	const dataConsumption = async (dataSent, isEndCall) => {
		const wastIntervalData = dataSent - wastIntervalDataSent;
		const consIntervalData = consDataReceived - consIntervalDataReceived;

		// Get current network information
		const networkInfo = getNetworkInfo();

		const params = {
			sessionId: sessionIdRef.current,
			subscriptionId: 0,
			userId: 0,
			roleId: 0,
			dataSent: parseFloat(wastIntervalData.toFixed(2)),
			dataReceived: parseFloat(consIntervalData.toFixed(2)),
			isConferenceCall: true,
			isEndCall,
		};

		logger.info('Data consumption tracking', {
			wastIntervalData,
			consIntervalData,
			isEndCall,
			networkType: networkInfo.connectionType,
			isCellular: networkInfo.isCellular,
			effectiveType: networkInfo.effectiveType
		});

		// Warn if using high data on cellular
		if (networkInfo.isCellular && (wastIntervalData > 5 || consIntervalData > 5)) {
			logger.warn('High data usage on cellular network', {
				dataSent: wastIntervalData,
				dataReceived: consIntervalData,
				networkInfo
			});
			//toast.warning(`High data usage: ${(wastIntervalData + consIntervalData).toFixed(1)} MB on cellular`);
		}

		try {
			const resp = await apis.instance.post(`${webAssistantSubscriptionDataUsage}`, params);
			if (resp.data.success) {
				wastIntervalDataSent = dataSent;
				consIntervalDataReceived = consDataReceived;
				logger.info('Data consumption tracked successfully', {
					dataSent: wastIntervalData,
					dataReceived: consIntervalData,
					networkType: networkInfo.connectionType,
					totalData: (wastIntervalData + consIntervalData).toFixed(2) + ' MB'
				});
			} else {
				const errorMessage = resp.data.error ? t(resp.data.error) : t("responseMsg.failure");
				logger.error('Data consumption tracking failed', {
					error: resp.data.error,
					params,
					networkInfo
				});
				// Don't surface background data-tracking failures to the user once the call is
				// ending/ended (e.g. session already expired server-side) — just log them.
				if (!isLeavingCallRef.current) toast.error(errorMessage);
			}
		} catch (error) {
			const errorMessage = error.response && error.response.data && error.response.data.message ? t(error.response.data.message) : t("responseMsg.error");
			logger.error('Data consumption API error', {
				error: error.message,
				response: error.response?.data,
				params,
				networkInfo
			});
			if (!isLeavingCallRef.current) toast.error(errorMessage);
		}
	};

	const changeMediaStream = async () => {
		const newFacingMode = isFrontCamera ? "environment" : "user";
		logger.logUserAction('camera_flip_initiated', {
			currentFacingMode: isFrontCamera ? "user" : "environment",
			targetFacingMode: newFacingMode
		});

		setIsFrontCamera((prevState) => !prevState);

		try {
			// AGORA: Properly switch camera by recreating the video track with new facingMode
			if (localVideoTrackAgora && agoraClient) {
				// Unpublish old video track
				await agoraClient.unpublish([localVideoTrackAgora]);
				localVideoTrackAgora.stop();
				localVideoTrackAgora.close();

				// Create new video track with switched camera
				const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
				const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
				const videoConfig = isMobile
					? { encoderConfig: { width: 640, height: 480, frameRate: 24, bitrateMin: 400, bitrateMax: 1000 }, optimizationMode: "detail" }
					: { encoderConfig: { width: 1280, height: 720, frameRate: 30, bitrateMin: 800, bitrateMax: 2000 }, optimizationMode: "detail" };

				localVideoTrackAgora = await AgoraRTC.createCameraVideoTrack({
					...videoConfig,
					facingMode: newFacingMode
				});

				// Publish new track
				await agoraClient.publish([localVideoTrackAgora]);

				// Play in local video element
				if (localVideoRef.current) {
					localVideoTrackAgora.play(localVideoRef.current);
				}

				// Update local video stream state for recording
				const newMediaStreamTrack = localVideoTrackAgora.getMediaStreamTrack();
				setLocalVideoStream(newMediaStreamTrack ? new MediaStream([newMediaStreamTrack]) : null);

				logger.info('Camera flip completed via Agora', {
					facingMode: newFacingMode,
					trackId: localVideoTrackAgora.getTrackId()
				});
			} else {
				// Fallback: direct getUserMedia (for cases where Agora track doesn't exist)
				const constraints = {
					video: { facingMode: newFacingMode },
				};
				const newStream = await navigator.mediaDevices.getUserMedia(constraints);
				if (mediaStream) {
					mediaStream.getTracks().forEach((track) => track.stop());
				}
				setMediaStream(newStream);

				if (localVideoRef.current) {
					localVideoRef.current.srcObject = newStream;
					setLocalVideoStream(newStream);
				}
				logger.info('Camera flip completed via getUserMedia fallback', { facingMode: newFacingMode });
			}
		} catch (error) {
			logger.error('Error switching camera', {
				error: error.message,
				targetFacingMode: newFacingMode
			});
			console.error("Error switching camera:", error);
			toast.error("Failed to switch camera. Please try again.");
		}
	};

	/**
	 * Request camera and microphone together from a user gesture. The temporary stream
	 * verifies browser permission and is released immediately before Agora creates tracks.
	 */
	const requestMediaAccess = async () => {
		logger.logUserAction('media_access_requested', { timestamp: new Date().toISOString() });
		setMediaAccessError("");
		if (window.isSecureContext === false) {
			setMediaAccessError("Camera and microphone access requires a secure HTTPS connection. Open the secure call link and try again.");
			return;
		}
		if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
			setMediaAccessError("This browser does not support camera and microphone calls. Update the browser or open the link in Safari on iPhone or Chrome on Android.");
			return;
		}

		try {
			// Begin loading Agora while the browser permission prompt is open. Once access
			// is granted, a separate explicit click can synchronously unlock Agora audio.
			const agoraSdkPromise = agoraSdk
				? Promise.resolve(agoraSdk)
				: import("agora-rtc-sdk-ng").then(module => module.default);
			const mediaAccessStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
			agoraSdk = await agoraSdkPromise;

			// Log details about the granted media stream
			const streamInfo = {
				active: mediaAccessStream.active,
				audioTracks: mediaAccessStream.getAudioTracks().map(track => ({
					enabled: track.enabled,
					muted: track.muted,
					readyState: track.readyState
				})),
				videoTracks: mediaAccessStream.getVideoTracks().map(track => ({
					enabled: track.enabled,
					muted: track.muted,
					readyState: track.readyState,
					width: track.getSettings()?.width,
					height: track.getSettings()?.height,
					frameRate: track.getSettings()?.frameRate,
					facingMode: track.getSettings()?.facingMode
				}))
			};

			logger.info('Media access granted with detailed stream info', {
				streamInfo,
				audioTrackCount: streamInfo.audioTracks.length,
				videoTrackCount: streamInfo.videoTracks.length
			});

			setMediaStream(mediaAccessStream);
			mediaStreamRef.current = mediaAccessStream; // Keep ref so initAgora can stop tracks before creating Agora tracks
			setShowMediaAccessPopup(false); // Hide popup on success
			hasExplicitAudioUnlockRef.current = false;
			setShowAudioResumePopup(true);
			sessionStorage.setItem("mediaAccessGranted", "true"); // Set flag to prevent showing again

			logger.info('Media access granted successfully', { timestamp: new Date().toISOString() });

			// Log updated device information after access granted
			await logAvailableMediaDevices();

			logger.info('Media access granted; waiting for explicit Agora audio unlock');
		} catch (err) {
			logger.error('Media access denied or failed', {
				error: err.message,
				errorName: err.name,
				constraint: err.constraint
			});
			if (err.name === "NotAllowedError" || err.name === "SecurityError") {
				setMediaAccessError("Camera and microphone access was blocked. Allow access in your browser's site settings, then try again.");
			} else if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
				setMediaAccessError("A working camera and microphone were not found. Connect or enable both devices, then try again.");
			} else if (err.name === "NotReadableError" || err.name === "AbortError") {
				setMediaAccessError("The camera or microphone is being used by another app. Close the other app, then try again.");
			} else {
				setMediaAccessError("Camera and microphone access could not be started. Check both devices, then try again.");
			}
		}
	};

	/** Resume Agora's own AudioContext from the explicit browser-required user gesture. */
	const resumeConsultantAudio = async () => {
		try {
			if (!agoraSdk) {
				throw new Error("Agora audio is not ready");
			}
			hasExplicitAudioUnlockRef.current = true;
			await agoraSdk.resumeAudioContext();
			setShowAudioResumePopup(false);
			sessionStorage.setItem("volumeConfirmed", "true");

			// On the first join this gesture can occur before the channel or consultant
			// track exists. The later user-published handler will use the unlocked context.
			if (!agoraClient) {
				logger.info('Agora audio unlocked before channel initialization');
				return;
			}
			const consultantUser = agoraClient?.remoteUsers?.find(user => user.uid === AGORA_UID_CONSULTANT)
				|| remoteUsers[AGORA_UID_CONSULTANT];
			if (!consultantUser || isConsultantAudioBlocked) {
				logger.info('Agora audio unlocked before consultant audio became available');
				return;
			}
			if (!consultantUser.audioTrack) {
				await agoraClient.subscribe(consultantUser, "audio");
			}
			if (!consultantUser.audioTrack) {
				throw new Error("Consultant audio track was not published");
			}
			if (typeof consultantUser.audioTrack.setPlaybackDevice === "function") {
				try {
					const devices = await navigator.mediaDevices.enumerateDevices();
					const audioOutputs = devices.filter(device => device.kind === "audiooutput");
					const preferredOutput = audioOutputs.find(device => device.deviceId === "communications")
						|| audioOutputs.find(device => /speaker|built-in|internal|realtek|headphone|headset/i.test(device.label)
							&& !/hdmi|displayport|display audio/i.test(device.label));
					await consultantUser.audioTrack.setPlaybackDevice(preferredOutput?.deviceId || "default");
				} catch (playbackDeviceError) {
					logger.warn('Unable to select an explicit Agora playback device; using browser default', {
						error: playbackDeviceError.message
					});
				}
			}
			const didStartPlayback = await playConsultantAudio(consultantUser.audioTrack, "user_resume");
			if (!didStartPlayback) {
				throw new Error("Consultant audio playback is still blocked");
			}
			setRemoteAudioTrack(consultantUser.audioTrack);
			logger.info('Consultant audio resumed after user interaction');
		} catch (error) {
			hasExplicitAudioUnlockRef.current = false;
			logger.error('Failed to resume consultant audio', { error: error.message });
			toast.error('Unable to start call audio. Please try again.');
		}
	};

	const stopMediaAccess = () => {
		if (mediaStream) {
			mediaStream.getTracks().forEach((track) => track.stop());
			setMediaStream(null); // Clear the stream reference
		}
	};

	// Helper to robustly attach and play audio stream
	// function attachAndPlayAudio(audioElement, stream) {
	// 	if (!audioElement || !stream) return;
	// 	audioElement.srcObject = stream;
	// 	audioElement.volume = 1.0;
	// 	audioElement.muted = false;
	// 	// Wait for canplay event before calling play
	// 	const playAudio = () => {
	// 		audioElement.play().catch(() => {
	// 			// Optionally show retry dialog/button here
	// 		});
	// 		audioElement.removeEventListener('canplay', playAudio);
	// 	};
	// 	audioElement.addEventListener('canplay', playAudio);
	// }

	// Initialize AudioContext if not already
	useEffect(() => {
		if (!audioContext) {
			const AC = window.AudioContext || window.webkitAudioContext;
			if (AC) setAudioContext(new AC());
		}
	}, [audioContext]);

	useEffect(() => () => {
		if (agoraSdk && agoraAutoplayFailedHandler) {
			agoraSdk.off("autoplay-failed", agoraAutoplayFailedHandler);
			agoraAutoplayFailedHandler = null;
		}
		if (agoraSdk && agoraAudioContextStateHandler) {
			agoraSdk.off("audio-context-state-changed", agoraAudioContextStateHandler);
			agoraAudioContextStateHandler = null;
		}
	}, []);

	return (
		<>
			<Dialog
				open={showAudioResumePopup}
				onClose={() => null}
				aria-labelledby="audio-resume-dialog-title"
			>
				<DialogTitle id="audio-resume-dialog-title">Enable call audio</DialogTitle>
				<DialogContent>
					Select the button below to hear the consultant during the call.
				</DialogContent>
				<DialogActions>
					<CadisButton onClick={resumeConsultantAudio} variant="contained" color="primary">
						Enable audio
					</CadisButton>
				</DialogActions>
			</Dialog>

			<Dialog
				open={showMediaAccessPopup}
				onClose={() => {
					return null;
				}}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description">
				<DialogTitle id="alert-dialog-title">{t("lbl_allowAccess")}</DialogTitle>
				<DialogContent>
					{t("lbl_toJoinVideoCall")}
					<ul style={{ marginBottom: 0 }}>
						<li>{t("lbl_camera")}</li>
						<li>{t("lbl_microphone")}</li>
					</ul>
					{mediaAccessError && (
						<Typography color="error" variant="body2" sx={{ mt: 2 }}>
							{mediaAccessError}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<CadisButton onClick={requestMediaAccess} variant="contained" color="primary">
						{t("lbl_allow")}
					</CadisButton>
				</DialogActions>
			</Dialog>

			{navigationBackPopup && (
				<Dialog
					open={navigationBackPopup && !endCallDialog}
					onClose={() => {
						return null;
					}}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description">
					<Box backgroundColor={"rgba(233, 57, 79, 0.2)"} color={"rgba(233, 57, 79, 1)"} padding={"8px 20px"}>
						<Typography variant="body2" fontWeight={"normal"}>
							<Typography display={"inline"} fontWeight={"bold"} variant="body2">
								Note:&nbsp;
							</Typography>
							{t("lbl_confirmLeaveCallWarning")}
						</Typography>
					</Box>
					<DialogTitle id="alert-dialog-title">{t("lbl_confirmation")}</DialogTitle>
					<DialogContent>{t("lbl_confirmLeaveCall")}</DialogContent>
					<DialogActions>
						<CadisButton variant="contained" color="error" onClick={handleLeaveCall}>
							{t("lbl_yes")}
						</CadisButton>
						<CadisButton variant="contained" color="success" onClick={handleStayInCall}>
							{t("lbl_no")}
						</CadisButton>
					</DialogActions>
				</Dialog>
			)}

			{/* User left alert dialog */}
			{userLeftAlert && (
				<CadisDialog
					open={userLeftAlert}
					title={`${state?.assistantName} left. You want to Stay in call ?`}
					DialogActions={
						<>
							<Button variant="contained" color="error" onClick={handleEndCall}>
								{t("lbl_endCallBtn")}
							</Button>
							<Button variant="contained" color="success" onClick={handleStayInCall}>
								{t("lbl_stay")}
							</Button>
						</>
					}
				/>
			)}

			{/* Open Invite Dialog */}
			{openInviteDialog()}
			{/* Open Reconnect Dialog */}
			{openReconnectDialog()}
			{/* Open End Call Popup */}
			{openEndCallPopup()}

			{/* Main container */}
			<Box pl={2} pr={2} boxSizing="border-box" backgroundColor="#222222" className="webAst-call-screen" display="block" width="100vw" ref={outerContainerRef}>
				<Box display="flex" flexDirection="column" height="100%" width="100%" className="call_container" boxSizing="border-box">
					{/* <> */}
					<Box
						sx={{
							minHeight: "40px", height: "40px", display: "flex", alignItems: "center", color: "#ffffff",
						}}>
						<Grid container direction={"row"} spacing={0} border={"solid 0 yellow"} width={"100%"} height={"100%"} margin={"0"} alignItems={"center"}>
							<Grid item xs>
								<Typography
									variant="h5"
									visibility={isRemoteVideo || isConsultantCameraOff ? "visible" : "hidden"}
									sx={{
										maxWidth: "95%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
									}}>
									{isRemoteVideo || isConsultantCameraOff ? <>{roomData?.consultantName}</> : null}
								</Typography>
							</Grid>
							<Grid item xs={"auto"}>
								{(!isRemoteVideo && !isConsultantCameraOff) || failedPong > 2 ? <Typography>Connecting...</Typography> : null}
							</Grid>

							<Grid item xs textAlign={"right"} display={"flex"} justifyContent={"end"} alignItems={"center"}>
								{recordingStarted && (
									<Box display={"inline-block"}>
										<Box display={"flex"} alignItems={"center"} className="recording-started-border" paddingX={0.6} paddingY={0} lineHeight="0">
											<Box component={"span"} sx={{ color: "error.main" }}>
												<FiberManualRecordIcon />
											</Box>
											<Box component={"span"}>
												<Typography color="#ffffff">{t("lbl_rec")}</Typography>
											</Box>
										</Box>
									</Box>
								)}
								<Typography display={"inline-block"} width={"100px"} variant="h5" fontSize={"large"} component={"h6"} sx={{ color: "#F2AF4C" }}>
									{hours < 10 ? "0" + hours : hours}:{minutes < 10 ? "0" + minutes : minutes}:{seconds < 10 ? "0" + seconds : seconds}
								</Typography>
							</Grid>
						</Grid>
					</Box>

					{/* Video container */}
					<Box display={"flex"} position={"relative"} height={"calc(100% - 84px)"}>
						<div className="video_container primary-video-container web-user-area">
							<audio
								id="consultantAudio"
								ref={remoteAudioRef}
								muted={false}
								videoOffState={false}
								autoPlay={true}
								onLoadStart={() => {
									handleAudioStateChange();
								}}
								onCanPlay={() => {
									handleAudioStateChange();
								}}
								onPlay={() => {
									handleAudioStateChange();
								}}
								onPause={() => {
									handleAudioStateChange();
								}}
								onVolumeChange={() => {
									handleAudioStateChange();
								}}
								onError={(e) => {
									console.error('Consultant audio error:', e);
									toast.error('Consultant audio playback error');
								}}
							/>
							<div id="container1" className="primary-video web-user-area">
								<video id="video4" ref={remoteVideoRef} muted={true} videoOffState={true} playsInline autoPlay className="glass_video" />
								{/* Consultant turned their camera off while still in the call — show an avatar.
								    It must follow the consultant feed across shuffle: showMuteIcon===false means
								    the consultant video is in this primary container (same signal used for the mute icon). */}
								{isConsultantCameraOff && !showMuteIcon ? (
									<Box
										sx={{
											position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
											display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
											gap: 1.5, zIndex: 2, pointerEvents: "none",
										}}>
										<Avatar sx={{ width: 96, height: 96, fontSize: "2.5rem", bgcolor: "#F2AF4C", color: "#222222" }}>
											{roomData?.consultantName ? roomData.consultantName.trim().charAt(0).toUpperCase() : ""}
										</Avatar>
										<Typography color="#ffffff" variant="h6">{roomData?.consultantName}</Typography>
									</Box>
								) : null}
								{/* Self (web assistant) camera-off avatar when the local feed is shuffled into this primary container */}
								{isSelfCameraOff && showMuteIcon ? (
									<Box
										sx={{
											position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
											display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
											gap: 1.5, zIndex: 2, pointerEvents: "none",
										}}>
										<Avatar sx={{ width: 96, height: 96, fontSize: "2.5rem", bgcolor: "#F2AF4C", color: "#222222" }}>
											{roomData?.webAssistantName ? roomData.webAssistantName.trim().charAt(0).toUpperCase() : ""}
										</Avatar>
										<Typography color="#ffffff" variant="h6">{roomData?.webAssistantName}</Typography>
									</Box>
								) : null}
								{/* <Box
									sx={{
										position: "absolute",
										top: "10%",
										width: "100%",
										textAlign: "center",
										fontWeight: 600,
										textShadow: `0 0 6px #fff, 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #fff`,
										fontSize: "1.2rem",
									}}>
									This is sample text sent by consultant to Web Assistant
								</Box> */}
								<Stack
									direction={"column"}
									justifyContent={"flex-start"}
									alignItems={"center"}
									gap={0.5}
									height={"60vh"}
									sx={{
										position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", maxWidth: "90%",
									}}>
									{textMessages.map((msg, index) => (
										<Chip
											key={msg.id}
											size="medium"
											label={msg.text}
											color="default"
											onDelete={() => handleClose(msg.id)}
											sx={{
												padding: ".2rem .3rem", boxShadow: "0 2px 8px rgba(0,0,0,0.2)", fontWeight: 500, background: "rgba(255, 255, 255, 0.8)", fontSize: "1rem", lineHeight: "1.2", borderRadius: "8px",
											}}></Chip>
									))}
								</Stack>
							</div>
							{isMuteConsultant && !showMuteIcon ? <CadisUnmuteIcon top="10px" left="49%" sx={{}} /> : ""}
							<Box position={"absolute"} bottom={"0"} right={"0"}>
								<Box
									sx={{
										position: "relative",
										padding: "1vmax",
										boxSizing: "border-box",
									}}
									className={`secondary-video-container picture-in-picture-video-container ${isSelfSecondary ? "self-view" : "user-view"}`}>
									<div id="container2" className="secondary-video">
										<video id="video5" ref={localVideoRef} muted={false} videoOffState={false} playsInline autoPlay backgroundColor={"red"} className="local_video" />
									</div>
									{/* Camera-off avatar in the PiP box when the consultant feed has been shuffled here */}
									{isConsultantCameraOff && showMuteIcon ? (
										<Box
											sx={{
												position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
												display: "flex", alignItems: "center", justifyContent: "center",
												zIndex: 2, pointerEvents: "none",
											}}>
											<Avatar sx={{ width: 48, height: 48, fontSize: "1.25rem", bgcolor: "#F2AF4C", color: "#222222" }}>
												{roomData?.consultantName ? roomData.consultantName.trim().charAt(0).toUpperCase() : ""}
											</Avatar>
										</Box>
									) : null}
									{/* Self (web assistant) camera-off avatar in the PiP box (default position of the local feed) */}
									{isSelfCameraOff && !showMuteIcon ? (
										<Box
											sx={{
												position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
												display: "flex", alignItems: "center", justifyContent: "center",
												zIndex: 2, pointerEvents: "none",
											}}>
											<Avatar sx={{ width: 48, height: 48, fontSize: "1.25rem", bgcolor: "#F2AF4C", color: "#222222" }}>
												{roomData?.webAssistantName ? roomData.webAssistantName.trim().charAt(0).toUpperCase() : ""}
											</Avatar>
										</Box>
									) : null}
									{isMuteConsultant && showMuteIcon ? <CadisUnmuteIcon bottom="calc(1vmax + 2px)" left="calc(1vmax + 2px)" /> : null}

									<Box
										sx={{
											position: "absolute", top: "calc(1vmax + 2px)", right: "calc(1vmax + 2px)", zIndex: "1000",
										}}>
										<Tooltip title={"Shuffle Video View"}>
											<IconButton aria-label="shuffle-video-icon" size="small" className="custom-default shuffle-video" color={"default"} disableRipple={true} onClick={swapVideos} sx={{ cursor: "pointer" }}>
												<span className="cad-shuffle-video rotate-temp"></span>
											</IconButton>
										</Tooltip>
									</Box>
								</Box>
							</Box>
						</div>
					</Box>

					{/* Bottom section */}
					<Grid container alignItems="center" minHeight={"42px"} height={"42px"} justifyContent="space-between">
						<Box sx={{ position: "relative" }} display={{ xs: "none", lg: "inline-block" }}>
							{/* empty box left */}
						</Box>

						{/* Mute/Unmute and video on/off button */}
						<Box display="inline-block" paddingX={{ lg: 1 }}>
							<ToggleComponent localVideoStream={localVideoStream} isVideo={true} socketRef={socketRef} recordingMute={recordingMute} setWebAsstSelfMute={setWebAsstSelfMute} setSelfVideoOff={setIsSelfCameraOff} localAudioTrackAgora={localAudioTrackAgora} localVideoTrackAgora={localVideoTrackAgora} /> {/* Video toggle */}
							<ToggleComponent isVideo={false} socketRef={socketRef} recordingMute={recordingMute} setWebAsstSelfMute={setWebAsstSelfMute} localAudioTrackAgora={localAudioTrackAgora} localVideoTrackAgora={localVideoTrackAgora} /> {/* Audio toggle */}
						</Box>

						{isMobileDevice ? (
							<IconButton aria-label={"Camera Flip"} size="small" onClick={changeMediaStream} className="custom-default" color="default">
								<span className="cad-switch-camera"></span>
							</IconButton>
						) : (
							""
						)}

						{/* Leave call button */}
						<CadisButton size="small" variant="contained" color="error" onClick={handleLeaveCall} sx={{ float: "right" }}>
							{t("lbl_leaveCall")}
						</CadisButton>
					</Grid>
				</Box>
			</Box>
		</>
	);
};
export default WebAssistantCallScreen;
